import React, { useState, useEffect, useRef } from 'react';
import * as LucideIcons from 'lucide-react';

// バージョン違いによるクラッシュを防ぐ安全なアイコンローダー
const getIcon = (names) => {
  for (const name of names) {
    if (LucideIcons[name]) return LucideIcons[name];
  }
  return () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;
};

const Sparkles = getIcon(['Sparkles', 'Star']);
const Smartphone = getIcon(['Smartphone', 'Phone']);
const Palette = getIcon(['Palette', 'Pen']);
const ShieldCheck = getIcon(['ShieldCheck', 'Shield']);
const ChevronRight = getIcon(['ChevronRight', 'ArrowRight']);
const Mail = getIcon(['Mail']);
const ScanLine = getIcon(['ScanLine', 'Camera']);
const CheckCircle = getIcon(['CircleCheck', 'CheckCircle', 'Check']);
const CheckCircle2 = getIcon(['CircleCheckBig', 'CheckCircle2', 'Check']);
const AlertCircle = getIcon(['CircleAlert', 'AlertCircle', 'AlertTriangle']);
const Upload = getIcon(['Upload', 'ArrowUp']);
const Package = getIcon(['Package', 'Box']);
const Layout = getIcon(['LayoutGrid', 'Layout', 'Grid']);
const Brush = getIcon(['Brush', 'Pen']);
const ImageIcon = getIcon(['Image', 'Camera']);
const Plus = getIcon(['Plus']);
const Minus = getIcon(['Minus']);
const X = getIcon(['X']);
const Loader2 = getIcon(['Loader2', 'Loader']);
const HeartHandshake = getIcon(['HeartHandshake', 'Heart']);
const Store = getIcon(['Store', 'Home']);
const Building2 = getIcon(['Building2', 'Building']);
const ExternalLink = getIcon(['ExternalLink', 'Link']);
const Cpu = getIcon(['Cpu', 'Settings']);
const Menu = getIcon(['Menu']);
const Info = getIcon(['Info']);
const HelpCircle = getIcon(['CircleHelp', 'HelpCircle', 'Info']);
const Camera = getIcon(['Camera']);
const ArrowRight = getIcon(['ArrowRight']);
const ChevronDown = getIcon(['ChevronDown']);
const ChevronUp = getIcon(['ChevronUp']);
const MousePointer2 = getIcon(['Pointer', 'MousePointer2']);
const User = getIcon(['User']);
const Phone = getIcon(['Phone']);
const MapPin = getIcon(['MapPin']);
const Dog = getIcon(['Dog', 'Smile']);
const ChevronLeft = getIcon(['ChevronLeft']);
const ShieldAlert = getIcon(['ShieldAlert', 'Shield']);
const Heart = getIcon(['Heart']);
const Key = getIcon(['Key']);
const Tag = getIcon(['Tag']);
const PencilRuler = getIcon(['PencilRuler', 'Pencil', 'Edit']);
const Download = getIcon(['Download']);
const Truck = getIcon(['Truck']);
const Search = getIcon(['Search']);
const Eye = getIcon(['Eye']);
const Award = getIcon(['Award', 'Star']);
const Star = getIcon(['Star']);
const Gift = getIcon(['Gift', 'Box']);
const Instagram = getIcon(['Instagram']);

// ==========================================
// テキスト整形ヘルパー（太字対応）
// ==========================================
const formatText = (text) => {
  if (!text) return null;
  // **bold** をパースして太字にする
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-black text-gray-900 border-b-2 border-red-200">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

// ==========================================
// データ定義 (FAQ, 利用規約)
// ==========================================
const faqData = [
  {
    category: "対応動物・対象について",
    q: "犬や猫以外の動物も制作できますか？",
    a: "はい、可能です。うさぎや鳥、ハムスターなどの小動物から、爬虫類まで幅広く対応しております。\nただし、写真の状態や対象の形状によってはAI生成が難しい場合がございます。その際は「写真の再提出」または「注文キャンセル」とさせていただく場合がございます。"
  },
  {
    category: "商品・再現について（重要）",
    q: "本当に似ますか？",
    a: "本サービスは、1枚のお写真をもとにAIで立体化する特性上、\n細部（しっぽの巻き方・マズル・毛並み・ポーズ等）まで完全に一致するものではありません。\n全体の雰囲気やバランスをもとに、\n“その子らしさ”を再現する仕様となっております。\nそのため、細部の差異ではなく、\n**「全体として違和感がないか」**という観点でお楽しみいただく商品です。"
  },
  {
    category: "商品・サービスについて",
    q: "どのような状態で届きますか？",
    a: "本商品は、お送りいただいた写真から3Dデータを生成し、高精細3Dプリンターで出力した「未塗装フィギュア」です。\n完成品ではなく、ご自身の手で彩色して命を吹き込んでいただく「体験型キット」となります。\n実際に色を重ねていただくことで、より“うちの子らしさ”が引き立ってまいります。"
  },
  {
    category: "プレビュー・確認について（最重要）",
    q: "完成イメージは事前に確認できますか？",
    a: "はい、可能です。\n制作前に「造形イメージ（スクリーンショット）」をご確認いただき、承認後に本制作へ進みます。\nただし本確認は、細部の修正を行うためのものではなく、\n**「全体のフォルムや雰囲気として問題がないか」**をご確認いただく工程となります。"
  },
  {
    category: "プレビュー・確認について（最重要）",
    q: "確認後に修正はできますか？",
    a: "AI生成の特性上、しっぽの形状・マズル・ポーズなど、\n細部の個別調整や大きな形状変更はできません。\n本サービスは\n「細部を調整していくオーダーメイド」ではなく、\n「全体の雰囲気や印象を楽しむプロダクト」となります。"
  },
  {
    category: "プレビュー・確認について（最重要）",
    q: "細かい部分が気になります。どこまでこだわれますか？",
    a: "お気持ちは大変よく分かりますが、\n本サービスは“完全再現”を目的としたサービスではありません。\n細部の差異を個別に調整していく形ではなく、\n**「全体としてその子らしさが感じられるか」**という観点で\nご判断いただく仕様となっております。"
  },
  {
    category: "写真について",
    q: "写真は何枚必要ですか？",
    a: "基本的には「最適な1枚」で制作可能です。\n全身（耳から尻尾まで）がはっきり写っている写真を推奨しております。\n※複数枚お送りいただいた場合でも、造形は1枚をベースに生成されます。"
  },
  {
    category: "写真について",
    q: "どのような写真が適していますか？",
    a: "【推奨】明るい自然光／全身が写っている／目線の高さが合っている写真\n【不向き】逆光・暗い・一部が隠れている・ピンボケやブレている写真\n写真の状態によって仕上がりが大きく変わるため、できるだけ条件の良いお写真をお選びください。"
  },
  {
    category: "制作・納期について",
    q: "納期はどれくらいですか？",
    a: "通常、プレビュー承認・決済完了から約2週間程度で発送いたします。\nプロ塗装オプションの場合は約1ヶ月程度お時間をいただきます。"
  },
  {
    category: "制作・納期について",
    q: "ポーズの変更はできますか？",
    a: "データ完成後のポーズ変更や修正はお受けできません。\n最初にお送りいただくお写真のポーズがそのまま立体化の基準となります。"
  },
  {
    category: "サイズ・塗装について",
    q: "どのサイズを選べばいいですか？",
    a: "迷われた場合は**Mサイズ（約6.5cm）**を推奨しています。\n塗りやすさ・サイズ感・満足度のバランスが最も良い標準サイズです。"
  },
  {
    category: "サイズ・塗装について",
    q: "塗装は難しいですか？",
    a: "基本的な塗料と筆が付属しているため、初めての方でも挑戦いただけますが、仕上がりには個人差がございます。"
  },
  {
    category: "プロ塗装について",
    q: "プロ塗装はどこまで再現されますか？",
    a: "毛並みや色合いを丁寧に表現し、“その子らしさ”を引き立てる仕上がりを目指します。\nただし、細部まで完全に一致するものではなく、全体の雰囲気や印象を大切にした表現となります。"
  },
  {
    category: "素材・品質について",
    q: "どのような素材ですか？",
    a: "高精細なUV硬化レジンを使用しています。滑らかな質感で塗装のノリが良いのが特徴です。"
  },
  {
    category: "素材・品質について",
    q: "気泡や小さな傷があります。",
    a: "3Dプリントの特性上、微細な積層跡・気泡・個体差が生じる場合があります。製造仕様の範囲内としてご了承ください。"
  },
  {
    category: "素材・品質について",
    q: "破損のリスクはありますか？",
    a: "レジン素材の特性上、強い衝撃により欠けや破損が生じる可能性があります。"
  },
  {
    category: "データについて",
    q: "3Dデータは保存されますか？",
    a: "セキュリティ保護のため、商品発送完了後にデータは削除されます。\n再注文の場合は再度制作（初回価格）となります。"
  },
  {
    category: "注文・キャンセルについて",
    q: "キャンセルや返品はできますか？",
    a: "プレビュー承認前であればキャンセル可能です。\n本制作開始後はオーダーメイド品のため、キャンセル・返品・返金はお受けできません。"
  }
];

const termsData = [
  {
    title: "第1条（本サービスの性質）",
    content: "本サービスで提供する商品は、利用者が提供した写真を基に3Dデータを生成し立体化した「未塗装フィギュアキット」です。\n利用者が自ら彩色を行うことで完成する「体験型商品」であることを確認するものとします。\nまた、本サービスは「完全な再現」を目的としたものではなく、\n“その子らしさ”を全体の雰囲気やバランスで表現する商品であることを、利用者はあらかじめ理解し同意するものとします。"
  },
  {
    title: "第2条（対象動物およびAI生成）",
    content: "1. 本サービスは犬・猫を主対象としますが、その他の動物についても対応可能です。\n2. 本商品はAI画像解析および3D造形技術を用いて制作されます。1枚の写真から立体を推測する特性上、実物との完全一致を保証するものではありません。\n3. 利用者は、造形の適否について、細部の一致ではなく全体の雰囲気やバランスに基づき判断するものとします。\n4. 造形データの完成後は、ポーズ変更・形状変更・部分的修正を含め、一切の修正を行うことはできません。"
  },
  {
    title: "第3条（プレビューと本制作）",
    content: "1. 当社は本制作（3Dプリント）の前に、造形イメージを画像にて利用者に提示（以下「プレビュー」）します。\n2. プレビューは細部の修正を目的とするものではなく、全体のフォルムや雰囲気として問題がないかを確認するためのものとします。\n3. 利用者がプレビューを承認し、決済を完了した時点で本契約が成立し、本制作を開始します。\n4. 承認および決済後の仕様変更、修正、キャンセルは、商品の性質上一切受け付けられません。"
  },
  {
    title: "第4条（画像データの権利および責任）",
    content: "1. 利用者は、本サービスに提供する画像について、適法な権利を有していることを保証するものとします。\n2. 無許可である他者の著作権、肖像権、商標権等を侵害する画像（第三者のペット、キャラクター等）の使用は禁止します。\n3. 画像の権利に関して第三者との間で紛争が生じた場合、利用者は自己の責任と費用において解決するものとし、当社は一切の責任を負いません。\n4. 提供された写真の内容、角度、状態等に起因する仕上がりの差異について、当社は責任を負いません。"
  },
  {
    title: "第5条（制作不可時の対応）",
    content: "お送りいただいた画像の状態や対象の形状等により、AI生成が困難であると当社が判断した場合、当社は写真の再提出を依頼、または注文をキャンセルすることができるものとします。"
  },
  {
    title: "第6条（製品仕様および免責）",
    content: "1. 3Dプリントおよびレジン素材の特性上、以下が発生する場合があります：\n　・微細な気泡\n　・積層跡\n　・表面の個体差\n　・サポート跡\n　・小さな欠け\nこれらは仕様であり返品・交換対象外です。\n2. 平面画像と立体の特性差により、見え方に差異が生じる場合があります。\n3. 目を閉じた写真であっても、立体として成立させるため目の造形が行われる場合があります。\n4. レジン素材は衝撃に弱く、破損の可能性があります。"
  },
  {
    title: "第7条（安全に関する注意）",
    content: "1. 本商品は観賞用および制作体験用です。\n2. 小さなパーツや細かな造形を含むため、誤飲・ケガの恐れがあります。\n3. 小さなお子様およびペットの手の届かない場所で保管・使用してください。\n4. 一部に細い・鋭利な箇所があるため、取り扱いには十分注意してください。\n5. 無理な力を加えた場合、破損や事故の原因となる場合があります。\n6. 塗料使用時は手・衣類・周囲への付着に注意し、適切な環境で使用してください。"
  },
  {
    title: "第8条（3Dデータの取り扱い）",
    content: "1. 当社はプライバシー保護のため、商品の発送完了をもって制作した3Dデータをサーバーから完全に削除します。\n2. データ削除後の再注文や修正依頼には応じられません。\n3. 当社から提供されるデジタルデータ（AR閲覧用等）の著作権は当社に帰属し、利用者は私的使用の範囲を超えて、商用利用や無断転載、第三者サービスへの利用等を行ってはならないものとします。"
  },
  {
    title: "第9条（キャンセル・返品）",
    content: "1. プレビュー承認および決済前であれば、利用者は注文をキャンセルすることができます。\n2. 本制作開始後はオーダーメイド品のため、利用者都合によるキャンセル、返品、交換、返金には一切応じられません。\n3. 到着した商品に、明らかな誤配や配送中の事故による著しい破損（粉砕等）がある場合に限り、到着後7日以内のお申し出に基づき、当社の判断により交換対応を行います。"
  },
  {
    title: "第10条（免責）",
    content: "通常の使用範囲を超えた取り扱いによる損害・事故について、当社は責任を負いません。"
  },
  {
    title: "第11条（規約の変更）",
    content: "当社は、必要と判断した場合には、利用者に通知することなく本規約を変更することができるものとします。"
  }
];

// ==========================================
// 共通部品
// ==========================================

// ★ Makuake実績バッジ (リンク付き)
const MakuakeBadge = () => (
  <a 
    href="https://www.makuake.com/project/uchinokofujikyouzai/" 
    target="_blank" 
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 md:gap-2.5 bg-white/95 backdrop-blur-sm border-[2px] md:border-[3px] border-yellow-400 p-1 pr-3 md:pr-5 rounded-full shadow-[0_4px_20px_rgba(250,204,21,0.4)] relative overflow-hidden group hover:scale-[1.02] transition-transform cursor-pointer"
  >
    {/* キラキラ光るエフェクト背景 */}
    <div className="absolute inset-0 w-[150%] h-full bg-gradient-to-r from-transparent via-white/80 to-transparent -skew-x-12 animate-shimmer z-0 pointer-events-none"></div>
    
    <div className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white text-[10px] md:text-xs font-black px-3 py-1.5 rounded-full tracking-widest flex items-center gap-1 shadow-sm shrink-0 relative z-10">
      <Award className="w-3.5 h-3.5 md:w-4 md:h-4" /> Makuake
    </div>
    <p className="text-gray-800 font-bold text-[11px] md:text-sm lg:text-[15px] tracking-tight shrink-0 relative z-10 flex items-center">
      先行販売で<span className="text-red-600 font-black text-sm md:text-lg lg:text-xl mx-0.5 md:mx-1 tracking-tighter drop-shadow-sm">目標達成率1549%</span>を記録！
    </p>
  </a>
);

const FloatingCTA = ({ targetId, onClick }) => {
  const [isPastHero, setIsPastHero] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => { setIsPastHero(window.scrollY > window.innerHeight * 0.7); };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { setIsFormVisible(entry.isIntersecting); }, { threshold: 0.1 });
    const target = document.getElementById(targetId);
    if (target) observer.observe(target);
    return () => { if (target) observer.unobserve(target); };
  }, [targetId]);

  if (!(isPastHero && !isFormVisible)) return null;

  return (
    <>
      {/* デスクトップ用 CTA */}
      <div className="hidden md:flex fixed right-6 bottom-6 z-40 transition-opacity duration-300">
        <button onClick={onClick} className="bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-[0_10px_30px_rgba(220,38,38,0.5)] transition-transform hover:-translate-y-2 w-[17rem] cursor-pointer border-2 border-white ring-4 ring-red-100">
          <div className="py-4 flex flex-col items-center justify-center h-full">
            <span className="text-white text-xs font-bold tracking-widest mb-1 leading-none opacity-90">＼ 2体目以降お得! ／</span>
            <span className="font-black text-base leading-none tracking-widest mt-1">今すぐ“うちの子”を形にする</span>
            <ChevronDown className="w-5 h-5 text-white mt-2 animate-bounce" />
          </div>
        </button>
      </div>
      {/* モバイル用 CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-5px_15px_rgba(0,0,0,0.05)] z-40 flex justify-center items-center transition-transform duration-300">
        <button onClick={onClick} className="bg-red-600 active:bg-red-700 text-white rounded-full shadow-[0_8px_25px_rgba(220,38,38,0.5)] w-full max-w-sm cursor-pointer border-2 border-white transition-transform active:scale-95 relative overflow-hidden">
          <div className="py-3 flex flex-col items-center justify-center w-full relative z-10">
            <span className="text-white text-[10px] font-bold tracking-widest leading-none mb-1 opacity-90">＼ 2体目以降お得! ／</span>
            <div className="flex items-center gap-1">
              <span className="font-black text-sm tracking-widest leading-none">今すぐ“うちの子”を形にする</span>
              <ChevronRight className="w-5 h-5 text-white shrink-0" />
            </div>
          </div>
          <div className="absolute inset-0 bg-white/20 animate-pulse pointer-events-none"></div>
        </button>
      </div>
    </>
  );
};

const CTABanner = ({ scrollToSection }) => (
  <div className="bg-red-600 py-16 px-4 text-center relative overflow-hidden my-8 shadow-inner">
    {/* 背景の装飾 */}
    <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMSIvPjwvc3ZnPg==')]"></div>
    <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center">
      {/* ★ ここにもMakuake実績バッジを追加 */}
      <div className="mb-6">
        <MakuakeBadge />
      </div>
      <p className="inline-block bg-white text-red-600 font-bold px-6 py-2 md:py-3 rounded-full text-sm md:text-base mb-6 shadow-md">＼ 迷ったらコレ！スマホから写真1枚送るだけ！ ／</p>
      <h2 className="text-white text-[26px] sm:text-3xl md:text-5xl font-black mb-10 tracking-tight leading-tight">
        世界に1つだけの<br className="md:hidden"/>オリジナルペットフィギュア
      </h2>
      <button onClick={() => scrollToSection('order-form')} className="bg-white text-red-600 hover:bg-gray-50 rounded-full py-4 px-8 flex items-center justify-center gap-2 md:gap-3 transition-transform hover:scale-105 shadow-[0_10px_25px_rgba(0,0,0,0.2)] w-full sm:max-w-lg mx-auto font-black text-lg md:text-xl tracking-widest border-4 border-white ring-4 ring-red-400">
        <span>今すぐ“うちの子”を形にする</span>
        <ChevronRight className="w-6 h-6 md:w-8 md:h-8 shrink-0 text-red-600" />
      </button>
    </div>
  </div>
);

// ★ Instagram用バナーコンポーネントを追加
const InstagramBanner = () => (
  <section className="py-12 bg-white border-b border-gray-200">
    <div className="max-w-4xl mx-auto px-4">
      <a 
        href="https://www.instagram.com/uchinoko_figure/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="block relative overflow-hidden rounded-[2rem] p-1 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] transition-transform hover:scale-[1.02] shadow-[0_10px_30px_rgba(253,29,29,0.15)] group"
      >
        <div className="bg-white rounded-[1.8rem] p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10 h-full">
          <div className="flex items-center gap-4 md:gap-6 w-full md:w-auto">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] rounded-full flex items-center justify-center shrink-0 shadow-inner">
              <Instagram className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="text-transparent bg-clip-text bg-gradient-to-br from-[#833AB4] to-[#FD1D1D] font-black text-[10px] md:text-sm tracking-widest mb-1 block">
                ＼ 最新の制作事例やお得な情報を発信中 ／
              </p>
              <h3 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight mb-1">
                公式Instagram
              </h3>
              <p className="text-gray-500 text-xs md:text-sm font-bold flex items-center gap-1">
                @uchinoko_figure
              </p>
            </div>
          </div>
          <div className="flex-shrink-0 w-full md:w-auto mt-2 md:mt-0">
            <span className="flex items-center justify-center gap-2 w-full md:w-auto bg-gray-900 text-white px-6 py-3.5 md:py-4 rounded-full font-bold text-sm md:text-base tracking-widest transition-colors group-hover:bg-gray-800 shadow-md">
              フォローしてチェック
              <ExternalLink className="w-4 h-4 md:w-5 md:h-5" />
            </span>
          </div>
        </div>
      </a>
    </div>
  </section>
);

const Accordion = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center py-4 md:py-6 text-left font-black text-gray-900 hover:text-red-600 transition-colors">
        <span className="pr-4 tracking-tight md:text-lg">{title}</span>
        {isOpen ? <ChevronUp className="w-6 h-6 flex-shrink-0 text-gray-500" /> : <ChevronDown className="w-6 h-6 flex-shrink-0 text-gray-400" />}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-gray-50 p-4 rounded-xl text-gray-700 text-sm md:text-base leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 色塗り体験ギミック (PaintExperience)
// ==========================================
const PaintExperience = () => {
  const [step, setStep] = useState('painting'); // 'painting', 'whiteout', 'video', 'whiteout_reverse'
  const [progress, setProgress] = useState(0); // 塗り率 (0-100)
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const isCompleted = useRef(false);
  const drawCount = useRef(0);

  const initCanvas = () => {
    if (step !== 'painting') return;
    isCompleted.current = false;
    drawCount.current = 0;
    setProgress(0);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const container = containerRef.current;
    
    // コンテナの実サイズを取得
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const img = new Image();
    img.crossOrigin = "Anonymous"; // 進行度計算(getImageData)のための設定
    img.onload = () => {
      // 親コンテナに対してカバー(object-fit: cover)するように描画
      const hRatio = canvas.width / img.width;
      const vRatio = canvas.height / img.height;
      const ratio = Math.max(hRatio, vRatio);
      const centerShift_x = (canvas.width - img.width * ratio) / 2;
      const centerShift_y = (canvas.height - img.height * ratio) / 2;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(img, 0, 0, img.width, img.height, centerShift_x, centerShift_y, img.width * ratio, img.height * ratio);
    };
    img.onerror = () => {
      // .pngで失敗した場合は.jpgへのフォールバックを試行
      if (!img.dataset.triedJpg) {
        img.dataset.triedJpg = true;
        img.src = "/image/15-1.jpg";
      } else {
        ctx.fillStyle = "#e5e7eb";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    };
    img.src = "/image/15-1.png"; // 白黒の未塗装画像
  };

  useEffect(() => {
    initCanvas();
    window.addEventListener('resize', initCanvas);
    return () => window.removeEventListener('resize', initCanvas);
  }, [step]);

  const draw = (e) => {
    if (isCompleted.current || step !== 'painting') return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // 座標取得 (マウス/タッチ対応)
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // なぞった部分を透明に抜く（下の着色画像が見えるようにする）
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    // 画面幅に応じてブラシサイズを調整 (なぞりやすく少し大きめ)
    const brushSize = Math.max(40, canvas.width * 0.1); 
    ctx.arc(x, y, brushSize, 0, Math.PI * 2);
    ctx.fill();
    
    drawCount.current++;

    // パフォーマンス考慮: 5回の描画ごとに進捗をチェック
    if (drawCount.current % 5 === 0) {
      checkProgress();
    }
  };

  const checkProgress = () => {
    if (isCompleted.current || step !== 'painting') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let transparentCount = 0;
      // ピクセルデータのアルファ値をチェック
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 128) transparentCount++; // 半透明以上になったピクセルをカウント
      }
      const totalPixels = data.length / 4;
      const actualPercent = (transparentCount / totalPixels) * 100;

      // 犬と猫の部分（全体の約35%程度）が塗られた状態を「表示上の100%」として扱う
      // これにより、背景まで塗らなくても早めに動画が再生されるようになります。
      const targetPercent = 35;
      let displayPercent = Math.floor((actualPercent / targetPercent) * 100);
      if (displayPercent > 100) displayPercent = 100;
      
      setProgress(displayPercent);

      // 100%になったら完了
      if (displayPercent >= 100) {
        isCompleted.current = true;
        handleComplete();
      }
    } catch(err) {
      // セキュリティエラー（CORS制限等）の場合は描画回数でフォールバック
      const fallbackMax = 150; // 犬と猫を塗る程度の回数に調整
      let displayPercent = Math.floor((drawCount.current / fallbackMax) * 100);
      if (displayPercent > 100) displayPercent = 100;
      
      setProgress(displayPercent);
      if (displayPercent >= 100) {
        isCompleted.current = true;
        handleComplete();
      }
    }
  };

  const handleComplete = () => {
    setProgress(100);
    setStep('whiteout');
    // 画面が真っ白(1000ms)になってから、動画をplayする
    setTimeout(() => {
      if (videoRef.current) {
        // playが実際に始まったら、白画面をフェードアウトさせて動画を見せる
        videoRef.current.play().then(() => {
          setStep('video');
        }).catch(e => {
          console.log("Video play error:", e);
          setStep('video');
        });
      } else {
        setStep('video');
      }
    }, 1000); 
  };

  const handleVideoEnd = () => {
    // 動画終了で再度ホワイトアウトし、初期状態（白黒）に戻る
    setStep('whiteout_reverse');
    setTimeout(() => {
       setStep('painting');
       initCanvas(); // progressのリセットもここに含まれる
    }, 1000);
  };

  // タッチ操作時の意図しないスクロールを防止
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const preventScroll = (e) => {
      if (step === 'painting') e.preventDefault();
    };
    container.addEventListener('touchmove', preventScroll, { passive: false });
    return () => container.removeEventListener('touchmove', preventScroll);
  }, [step]);

  return (
    <div className="max-w-4xl mx-auto mb-16 md:mb-24 px-0 md:px-4 relative">
      <div className="text-center mb-6">
        <span className="bg-red-600 text-white px-4 py-1.5 rounded-full text-xs font-bold mb-3 inline-flex items-center gap-1.5 tracking-widest uppercase shadow-sm animate-bounce">
          <Brush className="w-4 h-4"/> ペイント体験
        </span>
        <h3 className="text-lg md:text-xl font-black text-gray-900 tracking-tight">スマホでなぞるか、マウスを動かして色を塗ってみてください</h3>
      </div>
      
      <div 
        ref={containerRef} 
        className="w-full aspect-[4/3] md:aspect-video md:rounded-[2rem] overflow-hidden relative shadow-[0_10px_30px_rgba(0,0,0,0.1)] bg-gray-100 cursor-crosshair border-y md:border border-gray-200"
        style={{ touchAction: 'none' }}
      >
        {/* 進捗プログレスバー (ペイント中のみ表示) */}
        <div className={`absolute top-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm px-5 py-2 md:px-6 md:py-3 rounded-full shadow-lg z-40 flex items-center gap-2 md:gap-3 border border-gray-200 pointer-events-none transition-opacity duration-300 ${step === 'painting' && progress < 100 ? 'opacity-100' : 'opacity-0'}`}>
            <span className="text-gray-800 font-bold text-xs md:text-sm tracking-widest whitespace-nowrap">現在の完成度</span>
            <div className="w-20 md:w-32 h-2.5 md:h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 transition-all duration-200 ease-out" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="text-red-600 font-black text-sm md:text-lg w-9 md:w-10 text-right">{progress}%</span>
        </div>

        {/* 100%達成 強調表示 */}
        <div className={`absolute top-1/2 left-1/2 bg-red-600 px-8 py-5 md:px-12 md:py-6 rounded-[2rem] shadow-[0_15px_50px_rgba(220,38,38,0.6)] z-40 flex flex-col items-center justify-center border-4 border-white pointer-events-none transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] transform ${progress >= 100 && (step === 'painting' || step === 'whiteout') ? '-translate-x-1/2 -translate-y-1/2 scale-100 opacity-100' : '-translate-x-1/2 translate-y-10 scale-50 opacity-0'}`}>
            <span className="text-white/90 font-bold text-sm md:text-base tracking-widest mb-1 flex items-center gap-1.5"><Sparkles className="w-4 h-4 md:w-5 md:h-5"/>完成度</span>
            <span className="text-white font-black text-5xl md:text-6xl tracking-tighter drop-shadow-md">100%</span>
        </div>

        {/* 背景：着色画像 (15-2.png / jpgフォールバック対応) */}
        <img 
          src="/image/15-2.png" 
          alt="着色済み" 
          className="absolute inset-0 w-full h-full object-cover z-0" 
          onError={(e)=>{
            if (!e.target.dataset.triedJpg) {
               e.target.dataset.triedJpg = true;
               e.target.src = "/image/15-2.jpg";
            } else {
               e.target.src="https://placehold.co/1200x800/fcd34d/b45309?text=Colored+Image";
            }
          }}
        />
        
        {/* キャンバスレイヤー（初期は白黒の15-1.pngで覆われている） */}
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 w-full h-full z-10 transition-opacity duration-1000 ${step === 'painting' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onMouseMove={draw}
          onTouchStart={draw}
          onTouchMove={draw}
        />

        {/* 動画レイヤー (15-3.mp4) - 最初からマウントしておくことでラグを無くす */}
        <video
          ref={videoRef}
          src="/image/15-3.mp4"
          className={`absolute inset-0 w-full h-full object-cover z-20 transition-opacity duration-1000 ${step === 'video' || step === 'whiteout_reverse' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          playsInline
          muted
          preload="auto"
          onEnded={handleVideoEnd}
          onError={() => handleVideoEnd()}
        />

        {/* ホワイトアウト演出用オーバーレイ */}
        <div 
          className={`absolute inset-0 bg-white z-30 pointer-events-none transition-opacity duration-1000 ease-in-out ${step === 'whiteout' || step === 'whiteout_reverse' ? 'opacity-100' : 'opacity-0'}`}
        ></div>
      </div>
    </div>
  );
};

// ==========================================
// HomePage Component
// ==========================================
const HomePage = ({ navigateTo, scrollToSection }) => {
  return (
    <div className="bg-white tracking-tight pb-20 md:pb-0">
      
      {/* ヒーローセクション */}
      <section id="hero" className="relative md:min-h-[85svh] flex flex-col md:justify-center overflow-hidden bg-white md:bg-gray-50 pt-16 md:pt-20 pb-12 md:pb-16">
        
        {/* --- PC用の背景画像とグラデーション --- */}
        <div className="absolute inset-0 hidden md:block">
          {/* Heroの画像も極力早く読み込ませるため fetchpriority="high" を付与。おでこが見切れないよう object-top で上部を基準に切り抜き */}
          <img src="/image/1-1.png" alt="メインビジュアル" className="w-full h-full object-cover object-top" fetchpriority="high" onError={(e)=>{e.target.src="https://placehold.co/1920x1080"}} />
        </div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-white/95 via-white/70 to-transparent pointer-events-none hidden md:block md:w-3/4"></div>

        {/* --- スマホ用の画像配置（上部に画像全体を表示） --- */}
        <div className="w-full md:hidden relative -mt-4">
          <img src="/image/1-1.png" alt="メインビジュアル" className="w-full h-auto object-contain" fetchpriority="high" onError={(e)=>{e.target.src="https://placehold.co/1920x1080"}} />
          {/* 画像と下のコンテンツの境界をなじませるグラデーション */}
          <div className="absolute bottom-0 left-0 w-full h-1/4 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
        </div>

        {/* --- テキストコンテンツ（スマホ最適化） --- */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start mt-2 md:mt-0">
          
          {/* ★ 追加: Makuake実績バッジ（ファーストビューの目立つ位置に配置） ★ */}
          <div className="mb-4 md:mb-5">
            <MakuakeBadge />
          </div>

          {/* 見出しブロック */}
          <div className="mb-4 md:mb-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight text-left drop-shadow-[0_2px_4px_rgba(255,255,255,0.8)]">
              <span className="text-red-600 block mb-0.5 md:mb-2 text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-widest drop-shadow-sm">写真1枚で、</span>
              “うちの子”を立体に。
            </h1>
            <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-800 mt-2 drop-shadow-[0_2px_4px_rgba(255,255,255,0.8)] bg-white/50 md:bg-transparent inline-block rounded">
              自分で仕上げる、世界にひとつのフィギュア。
            </p>
          </div>

          {/* 価格とアクションを最優先でグループ化 */}
          <div className="w-full max-w-md bg-white/95 md:bg-white backdrop-blur-sm md:backdrop-blur-none p-5 md:p-6 rounded-[1.5rem] shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-gray-100 md:border-l-8 md:border-l-red-600 mb-5">
            <div className="flex items-end gap-2 mb-4">
              <span className="bg-red-600 text-white text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-sm tracking-widest leading-none mb-1">オーダーメイド</span>
              <p className="text-red-600 font-black text-[40px] md:text-5xl leading-none tracking-tighter">
                ¥3,800<span className="text-sm md:text-lg font-bold ml-1 text-gray-900 tracking-normal">から</span>
              </p>
            </div>
            
            <button onClick={()=>scrollToSection('order-form')} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full py-3.5 md:py-4 flex items-center justify-center gap-2 transition-transform hover:scale-105 shadow-[0_8px_20px_rgba(220,38,38,0.4)] text-base md:text-lg font-black tracking-widest border-2 border-white ring-2 ring-red-100">
              今すぐ“うちの子”を形にする
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* 補足情報（安心感）は短縮して下部に配置 */}
          <div className="w-full max-w-md bg-gray-50/90 md:bg-white/90 backdrop-blur-sm p-4 rounded-[1rem] border border-gray-100">
             <h3 className="text-xs md:text-sm font-black text-gray-900 mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-red-600 shrink-0" /> 届いたその日から完成できる
            </h3>
            <ul className="text-[11px] md:text-xs font-bold text-gray-600 space-y-1.5 ml-5 list-disc marker:text-gray-300">
              <li>模型店厳選の「高級筆」と「塗料」セット</li>
              <li>迷わず塗れる「色の組み合わせガイド」付き</li>
            </ul>
            <p className="text-xs md:text-sm text-red-600 font-black mt-3 pt-2 border-t border-gray-200/60 text-center tracking-tight">
              「ちゃんと完成できる安心感」までセットに。
            </p>
          </div>

          {/* 仕様の明記（前倒し） */}
          <div className="w-full max-w-md mt-3 bg-yellow-50/90 md:bg-yellow-50 backdrop-blur-sm p-3 rounded-[1rem] border border-yellow-200 shadow-sm">
            <p className="text-[11px] md:text-xs font-bold text-yellow-800 leading-relaxed flex items-start gap-1.5">
              <AlertCircle className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
              <span>※本サービスは写真1枚をもとにAIで立体化するため、細部は完全一致ではなく“その子らしさ”を再現する仕様です。</span>
            </p>
          </div>

        </div>
      </section>

      {/* 権威性バッジ */}
      <div className="bg-white border-y border-gray-200 py-4 w-full relative z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-4 md:gap-8">
          <span className="text-gray-800 font-bold text-xs md:text-sm flex items-center gap-2"><Award className="w-5 h-5 text-red-600"/> プロモデラー監修</span>
          <span className="text-gray-800 font-bold text-xs md:text-sm flex items-center gap-2"><Store className="w-5 h-5 text-red-600"/> 1954年創業 老舗模型店</span>
          <span className="text-gray-800 font-bold text-xs md:text-sm flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-red-600"/> 安心プレビュー採用</span>
        </div>
      </div>

      <section id="story" className="py-12 md:py-16 bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="mb-6 md:mb-8">
            <img 
              src="/image/14-1.png" 
              alt="スマホの中の写真" 
              className="w-full max-w-xs md:max-w-sm mx-auto rounded-[2rem] shadow-lg border border-gray-100 object-cover" 
              onError={(e)=>{
                if (!e.target.dataset.triedJpg) {
                  e.target.dataset.triedJpg = true;
                  e.target.src = "/image/14-1.jpg";
                } else {
                  e.target.onerror = null; 
                  e.target.src="https://placehold.co/800x800?text=Photo";
                }
              }} 
            />
          </div>
          
          <div className="space-y-4 md:space-y-6 text-xl md:text-3xl font-black tracking-tight leading-snug md:leading-normal text-gray-800">
            <p>
              スマホの中に、<br />
              何百枚もある“うちの子”。
            </p>
            <p>
              でも、<br />
              見返す機会は、意外と少ない。
            </p>
            <p className="text-2xl md:text-4xl text-red-600">
              だからこそ、<br />
              “形”として残したいと思いました。
            </p>
          </div>
        </div>
      </section>

      <section id="before-after" className="py-16 md:py-24 bg-gray-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block uppercase">Before / After</span>
          <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-6 tracking-tight">“その子らしさ”を立体に。<br className="md:hidden"/>写真から“雰囲気”をカタチに</h2>
          <p className="text-gray-700 font-bold mb-4 text-sm md:text-base">正面からの写真1枚から、全体のフォルムや愛らしい雰囲気を捉えて立体化します。</p>
          
          {/* 追加：完全一致ではないことの注意書き */}
          <div className="inline-flex items-center gap-1.5 bg-yellow-50 text-yellow-700 px-4 py-2 rounded-lg text-xs md:text-sm font-bold mb-10 mx-auto border border-yellow-200">
            <AlertCircle className="w-4 h-4 shrink-0" />
            ※完全一致ではなく、雰囲気再現のイメージです
          </div>

          <div className="bg-white p-2 rounded-[2rem] shadow-sm border border-gray-200 max-w-4xl mx-auto">
            <img src="/image/2-1.png" alt="完成イメージ" className="w-full h-auto rounded-[1.5rem] mx-auto" fetchpriority="high" onError={(e)=>{e.target.src="https://placehold.co/1200x800"}} />
          </div>
        </div>
      </section>

      {/* ★ Instagramバナーを配置（事例を見た後に誘導） */}
      <InstagramBanner />

      <section id="quality" className="py-16 md:py-24 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block uppercase">Professional Quality</span>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-4 tracking-tight">プロモデラー監修！高品質な<br className="md:hidden"/>3Dペットフィギュア制作の秘密</h2>
            <p className="text-gray-600 text-sm md:text-base">1954年創業の老舗模型店とトップモデラーが、品質を支えます。</p>
          </div>
          
          <div className="bg-gray-50 p-6 md:p-10 rounded-[2rem] border border-gray-200 shadow-sm">
            <div className="flex flex-col gap-8 md:gap-10">
              <div className="text-center max-w-3xl mx-auto">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-red-600 rounded-full flex items-center justify-center text-white shrink-0 shadow-md"><Cpu className="w-6 h-6 md:w-8 md:h-8"/></div>
                  <h3 className="font-black text-2xl md:text-3xl text-gray-900">職人の手によるモデリング調整</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-base md:text-lg">AIがベースを作った後、熟練のモデラーが手作業で全体のバランスを整え、その子“らしさ”を引き出します。</p>
              </div>
              <div className="grid md:grid-cols-2 gap-6 md:gap-8 w-full">
                <div className="relative group aspect-video">
                  <img src="/image/5-1.png" alt="職人技" className="w-full h-full object-cover rounded-[1.5rem] border border-gray-200" onError={(e)=>{e.target.src="https://placehold.co/800x600"}} />
                </div>
                <div className="relative group aspect-video">
                  <img src="/image/5-2.png" alt="3Dモデリング作業風景" className="w-full h-full object-cover rounded-[1.5rem] border border-gray-200" onError={(e)=>{e.target.src="https://placehold.co/800x600"}} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="size-price" className="py-16 md:py-24 bg-gray-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16 md:mb-20">
            <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block uppercase">Lineup & Specs</span>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-6 tracking-tight">サイズと価格</h2>
            <p className="text-gray-600 font-bold max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              用途に合わせて、3つのサイズをご用意しています。<br />
              細部の塗りやすさと存在感のバランスから<br className="md:hidden"/>
              「Mサイズ」が一番人気です。
            </p>
            <div className="inline-block mt-5 md:mt-6 px-6 py-2 bg-red-50 border-2 border-red-200 text-red-600 rounded-full font-black text-base md:text-lg tracking-widest shadow-sm">
              ＼ 迷ったらMサイズ！ ／
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 items-end pt-8 md:pt-12">
            <div className="bg-white rounded-[2rem] p-6 border border-gray-200 text-center flex flex-col h-full">
              <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-1">Sサイズ</h3>
              <p className="text-gray-500 text-xs font-bold tracking-widest mb-4">約3.5cm</p>
              <div className="aspect-[4/3] bg-gray-50 rounded-2xl border border-gray-100 shadow-sm mb-6 flex items-end justify-center pb-0 relative overflow-hidden"><img src="/image/3-1.png" alt="Sサイズ" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/400x300"}}/></div>
              <p className="text-3xl font-black text-gray-900 mb-4 tracking-tighter">¥3,800</p>
              <p className="text-sm text-gray-600 flex-1 text-left leading-relaxed">そっと寄り添う、コンパクトモデル。<br/><br/><span className="text-xs text-gray-500">※小さいため塗装の難易度は高めです。キーホルダー等におすすめ。</span></p>
            </div>
            {/* 一番人気 Mサイズ */}
            <div className="bg-white rounded-[2rem] p-8 border-2 border-red-600 text-center relative shadow-lg transform md:-translate-y-4 flex flex-col h-full z-20">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white font-bold px-6 py-1.5 rounded-full text-xs tracking-widest whitespace-nowrap">Best Choice / 一番人気</div>
              <div className="pt-4 flex flex-col h-full">
                <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-1">Mサイズ</h3>
                <p className="text-gray-500 text-xs font-bold tracking-widest mb-4">約6.5cm</p>
                <div className="aspect-[4/3] bg-gray-50 rounded-2xl border border-gray-100 mb-6 flex items-end justify-center pb-0 relative overflow-hidden"><img src="/image/3-2.png" alt="Mサイズ" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/400x300"}}/></div>
                <p className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tighter">¥6,800</p>
                <p className="text-sm text-gray-700 flex-1 text-left leading-relaxed font-bold">細部の再現性と塗りやすさが最高のバランス。<br/><br/>初めての方でも楽しく塗れる、最もおすすめのサイズです。</p>
                <button onClick={()=>scrollToSection('order-form')} className="mt-6 w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-xl shadow-[0_5px_15px_rgba(220,38,38,0.4)] transition-transform hover:-translate-y-1 tracking-widest">Mサイズを注文する</button>
              </div>
            </div>
            <div className="bg-white rounded-[2rem] p-6 border border-gray-200 text-center flex flex-col h-full">
              <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-1">Lサイズ</h3>
              <p className="text-gray-500 text-xs font-bold tracking-widest mb-4">約10.0cm</p>
              <div className="aspect-[4/3] bg-gray-50 rounded-2xl border border-gray-100 shadow-sm mb-6 flex items-end justify-center pb-0 relative overflow-hidden"><img src="/image/3-3.png" alt="Lサイズ" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/400x300"}}/></div>
              <p className="text-3xl font-black text-gray-900 mb-4 tracking-tighter">¥13,800</p>
              <p className="text-sm text-gray-600 flex-1 text-left leading-relaxed">圧倒的な存在感で残す、メモリアルモデル。<br/><br/><span className="text-xs text-gray-500">※表面積が広いため、筆塗りの際は丁寧な作業が必要になります。</span></p>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-8">※フィギュアのサイズは長辺最大サイズ基準です（犬は高さ、鳥は翼、爬虫類は全長など）。</p>
        </div>
      </section>

      <CTABanner scrollToSection={scrollToSection} />

      <section id="photo-guide" className="py-16 md:py-24 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block uppercase">Photo Guide</span>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 tracking-tight">高品質なフィギュアを作るための<br className="md:hidden"/>写真の選び方</h2>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              AI生成の特性上、写真の状態によって<br className="md:hidden"/>仕上がりが大きく変わります。<br className="hidden md:block"/>
              以下のポイントをご確認のうえ、<br className="md:hidden"/>とびきりの1枚をお選びください。
            </p>
          </div>
          <div className="bg-gray-50 p-2 rounded-[2rem] shadow-sm border border-gray-200 max-w-4xl mx-auto">
            <img src="/image/4-1.png" alt="写真の選び方" className="w-full h-auto rounded-[1.5rem] mx-auto" onError={(e)=>{e.target.src="https://placehold.co/1200x800"}} />
          </div>
        </div>
      </section>

      <section id="volume-discount" className="py-16 md:py-24 bg-gray-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 bg-red-600 text-white rounded-full text-xs font-bold tracking-widest mb-6 shadow-sm">＼ 2体目以降がお得 ／</span>
            <h2 className="text-2xl md:text-4xl font-black mb-6 tracking-tight leading-tight text-gray-900">追加造形・同時注文限定価格</h2>
            <p className="text-gray-700 leading-relaxed max-w-2xl mx-auto text-sm md:text-base mb-6">
              1度のデータ制作で複数のサイズを<br className="md:hidden"/>同時に作り出せるため、<br className="hidden md:block"/>
              同一データを使用した2体目以降は<br className="md:hidden"/>大幅なコストダウンが可能です。
            </p>
            
            {/* 今買う理由の訴求強化 */}
            <div className="inline-block bg-white border border-red-200 rounded-xl p-4 md:p-6 mb-8 text-center max-w-2xl mx-auto w-full shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-red-500"></div>
              <p className="text-red-600 font-black text-base md:text-xl mb-2 tracking-tight flex items-center justify-center gap-1.5 md:gap-2">
                <AlertCircle className="w-5 h-5 md:w-6 md:h-6 shrink-0" /> 後からの追加は「初回価格」になります
              </p>
              <p className="text-gray-800 font-bold text-sm md:text-base leading-relaxed">
                データは発送後に削除されるため、<br className="md:hidden" />
                <span className="text-red-600 bg-red-50 px-1.5 md:px-2 py-0.5 rounded font-black border-b-2 border-red-500 mt-1 inline-block">今まとめてご注文いただくのが最もお得</span>です。
              </p>
            </div>
            
            {/* 通常価格の表示 */}
            <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-2xl mx-auto mb-10">
              <div className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 shadow-sm flex flex-col items-center">
                 <p className="text-gray-600 font-bold text-xs md:text-sm mb-1">Sサイズ(追加)</p>
                 <p className="text-gray-400 text-[10px] md:text-xs font-bold line-through mb-0.5">通常 ¥3,800</p>
                 <p className="text-xl md:text-3xl font-black text-red-600 mb-1 tracking-tighter">¥1,800</p>
                 <p className="text-[10px] md:text-xs font-bold text-red-600 bg-red-50 rounded-full px-2 py-0.5 mt-auto border border-red-100">¥2,000 お得</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 shadow-sm border-b-4 border-b-red-500 flex flex-col items-center relative">
                 <div className="absolute -top-2.5 bg-red-500 text-white text-[9px] md:text-[10px] font-bold px-3 py-0.5 rounded-full shadow-sm tracking-widest">一番人気</div>
                 <p className="text-gray-600 font-bold text-xs md:text-sm mb-1 mt-1">Mサイズ(追加)</p>
                 <p className="text-gray-400 text-[10px] md:text-xs font-bold line-through mb-0.5">通常 ¥6,800</p>
                 <p className="text-xl md:text-3xl font-black text-red-600 mb-1 tracking-tighter">¥3,800</p>
                 <p className="text-[10px] md:text-xs font-bold text-white bg-red-600 rounded-full px-2 py-0.5 mt-auto shadow-sm">¥3,000 お得</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 shadow-sm flex flex-col items-center">
                 <p className="text-gray-600 font-bold text-xs md:text-sm mb-1">Lサイズ(追加)</p>
                 <p className="text-gray-400 text-[10px] md:text-xs font-bold line-through mb-0.5">通常 ¥13,800</p>
                 <p className="text-xl md:text-3xl font-black text-red-600 mb-1 tracking-tighter">¥8,800</p>
                 <p className="text-[10px] md:text-xs font-bold text-red-600 bg-red-50 rounded-full px-2 py-0.5 mt-auto border border-red-100">¥5,000 お得</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="options" className="py-16 md:py-24 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block uppercase">Options</span>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-4 tracking-tight">楽しみ方を広げるオプション</h2>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              飾る・持ち歩く・贈る、<br className="md:hidden"/>様々なオプションをご用意しています。
            </p>
          </div>

          {/* プレミアムオプション：プロ塗装仕上げ */}
          <div className="bg-gray-50 border-2 border-red-200 rounded-[2rem] p-6 md:p-10 mb-10 md:mb-16 flex flex-col md:flex-row gap-8 md:gap-12 items-center shadow-lg relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-red-600"></div>
            <div className="w-full md:w-1/2">
              <div className="aspect-[4/3] rounded-[1.5rem] overflow-hidden border border-gray-200">
                <img src="/image/5-3.png" alt="プロの塗装" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/800x600"}} />
              </div>
            </div>
            <div className="w-full md:w-1/2 flex flex-col text-gray-900">
              <span className="text-red-600 font-bold tracking-widest text-xs mb-2 uppercase flex items-center gap-2"><Award className="w-4 h-4"/> Premium Option</span>
              <h3 className="font-black text-2xl md:text-3xl mb-2 tracking-tight">プロ塗装仕上げ</h3>
              <p className="text-red-600 font-bold text-sm md:text-base mb-4 tracking-tight">「うちの子」が、最高クオリティで蘇る</p>
              <p className="text-gray-700 leading-relaxed text-sm md:text-base mb-6">
                自分で塗るのが不安な方も安心。<br/>
                ガンプラ日本一モデラーが彩色を監修し、<br className="md:hidden"/>
                全体の雰囲気を大切に、“その子らしさ”を引き立てる仕上げを行います。
              </p>
              <div className="flex items-end justify-between border-t border-gray-200 pt-6">
                <span className="font-bold">オプション追加料金</span>
                <span className="text-2xl md:text-4xl font-black text-red-600">¥10,000</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* ①木製台座 */}
            <div className="bg-white rounded-[1.5rem] border border-gray-200 overflow-hidden flex flex-col shadow-sm">
              <div className="aspect-video relative overflow-hidden bg-gray-50">
                <img src="/image/7-1.png" alt="木製台座" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="border-b border-gray-200 mb-4 pb-3">
                  <h3 className="font-black text-lg text-gray-900 mb-1">木製台座</h3>
                  <p className="text-red-600 font-bold text-xs tracking-tight">一生の宝物として飾れる</p>
                </div>
                <div className="space-y-3 mb-6 flex-1 text-sm text-gray-700">
                  <div className="flex justify-between items-start"><span className="font-bold">Sサイズ</span><span className="font-black">¥2,500</span></div>
                  <div className="flex justify-between items-start"><span className="font-bold">Mサイズ</span><span className="font-black">¥4,000</span></div>
                  <div className="flex justify-between items-start"><span className="font-bold">Lサイズ</span><span className="font-black">¥6,000</span></div>
                </div>
              </div>
            </div>

            {/* ②キーホルダー加工 */}
            <div className="bg-white rounded-[1.5rem] border border-gray-200 overflow-hidden flex flex-col shadow-sm">
              <div className="aspect-video relative overflow-hidden bg-gray-50">
                <img src="/image/7-2.png" alt="キーホルダー加工" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="border-b border-gray-200 mb-4 pb-3">
                  <h3 className="font-black text-lg text-gray-900 mb-1">キーホルダー加工</h3>
                  <p className="text-red-600 font-bold text-xs tracking-tight">いつでもどこでも持ち歩ける</p>
                </div>
                <p className="text-sm text-gray-600 mb-6 flex-1">お気に入りのバッグや鍵と一緒に。</p>
                <div className="text-right"><span className="text-xl font-black text-red-600">¥500</span></div>
              </div>
            </div>

            {/* ③ストラップ加工 */}
            <div className="bg-white rounded-[1.5rem] border border-gray-200 overflow-hidden flex flex-col shadow-sm">
              <div className="aspect-video relative overflow-hidden bg-gray-50 flex">
                <img src="/image/7-3-1.png" alt="ストラップ加工1" className="w-1/2 h-full object-cover border-r border-gray-100" onError={(e)=>{e.target.src="https://placehold.co/300x400"}} />
                <img src="/image/7-3-2.png" alt="ストラップ加工2" className="w-1/2 h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/300x400"}} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="border-b border-gray-200 mb-4 pb-3">
                  <h3 className="font-black text-lg text-gray-900 mb-1">ストラップ加工</h3>
                  <p className="text-red-600 font-bold text-xs tracking-tight">いつも手元で寄り添う</p>
                </div>
                <p className="text-sm text-gray-600 mb-6 flex-1">スマホやポーチのアクセントに。</p>
                <div className="text-right"><span className="text-xl font-black text-red-600">¥500</span></div>
              </div>
            </div>

            {/* ④GLBデータ */}
            <div className="bg-white rounded-[1.5rem] border border-gray-200 overflow-hidden flex flex-col shadow-sm">
              <div className="aspect-video relative overflow-hidden bg-gray-50 flex">
                <img src="/image/7-4-1.png" alt="GLBデータ1" className="w-1/2 h-full object-cover border-r border-gray-100" onError={(e)=>{e.target.src="https://placehold.co/300x400"}} />
                <img src="/image/7-4-2.png" alt="GLBデータ2" className="w-1/2 h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/300x400"}} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="border-b border-gray-200 mb-4 pb-3">
                  <h3 className="font-black text-lg text-gray-900 mb-1">GLBデータ</h3>
                  <p className="text-red-600 font-bold text-xs tracking-tight">デジタル空間でもずっと一緒</p>
                </div>
                <p className="text-sm text-gray-600 mb-6 flex-1">
                  メタバースでも、“うちの子”とずっと一緒に。<br/>
                  VRChatやclusterでアバターとして使える3Dデータです。<br/>
                  万が一の破損時も、何度でも再製作できます。
                </p>
                <div className="text-right"><span className="text-xl font-black text-red-600">¥6,000</span></div>
              </div>
            </div>

            {/* ⑥メモリアルギフトBOX */}
            <div className="bg-white rounded-[1.5rem] border border-gray-200 overflow-hidden flex flex-col shadow-sm">
              <div className="aspect-video relative overflow-hidden bg-gray-50 flex items-center justify-center border-b border-gray-200">
                 <img src="/image/7-6.png" alt="メモリアルギフトBOX" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="border-b border-gray-200 mb-4 pb-3">
                  <h3 className="font-black text-lg text-gray-900 mb-1">メモリアルギフトBOX</h3>
                  <p className="text-red-600 font-bold text-xs tracking-tight">大切な人へ、そのまま渡せる</p>
                </div>
                <p className="text-sm text-gray-600 mb-6 flex-1">届いてすぐに感動を共有できる、特別なギフト包装です。</p>
                <div className="text-right"><span className="text-xl font-black text-red-600">¥1,000</span></div>
              </div>
            </div>

          </div>

          {/* 送料を統合した注意事項 */}
          <div className="bg-white p-5 md:p-6 rounded-[1.5rem] text-left text-sm text-gray-700 border border-gray-200 shadow-sm mt-12">
            <h4 className="font-black text-gray-900 mb-4 flex items-center gap-2 pb-2 border-b border-gray-100"><AlertCircle className="w-5 h-5 text-red-600"/> ご注文・配送に関する注意事項</h4>
            <ul className="list-disc pl-5 space-y-3 font-bold text-xs md:text-sm text-gray-600">
              <li>フィギュアのサイズは長辺最大（犬なら高さ、鳥なら翼など）を基準とします。</li>
              <li><span className="text-red-600 font-black">「2体目以降」の特別価格は、初回注文時の同時注文限定となります。</span></li>
              {/* 送料の情報を目立たせて追加 */}
              <li className="bg-gray-50 p-3 rounded-lg border border-gray-100 mt-2 flex flex-col gap-1 list-none -ml-5">
                <span className="text-gray-900 flex items-center gap-1"><Truck className="w-4 h-4 text-red-600"/> 梱包・送料について：</span>
                <span className="pl-5">全国一律 <span className="text-red-600 font-black text-base">¥880</span>（沖縄・離島を除く）</span>
                <span className="text-[10px] md:text-xs font-normal mt-1 pl-5">※沖縄・離島への配送は、通常送料とは別に追加送料が発生いたします（決済時に加算）。</span>
              </li>
              <li>セキュリティ・プライバシー保護のため、データは商品発送完了後に削除されます。そのため、後日の追加注文は一からの制作（初回価格）となりますのでご注意ください。</li>
            </ul>
          </div>

        </div>
      </section>

      {/* 体験価値セクションとAR体験をオプションと制作の流れの間に配置 */}
      <section id="core-value" className="py-16 md:py-24 bg-gray-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block uppercase">Core Value</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 tracking-tight leading-tight">
              最後の仕上げは、<br className="md:hidden"/>飼い主の手で。
            </h2>
            <p className="text-xl md:text-2xl font-black text-red-600 mb-6 tracking-tight">
              最後に命を吹き込むのは、“あなた”です。
            </p>
            <p className="text-gray-800 font-bold text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-8">
              世界で一番その子を知っているのは、他の誰でもないあなた自身。<br className="hidden md:block" />
              少しずつ色を重ねて命を吹き込んでいくその時間も、<br className="md:hidden"/>かけがえのない思い出になります。
            </p>
            <div className="bg-white p-6 md:p-8 rounded-[1.5rem] border border-gray-200 inline-block text-left shadow-sm">
              <ul className="space-y-4 text-gray-700 font-bold text-sm md:text-base">
                <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-red-500 shrink-0"/> 筆や塗料もセットでお届け</li>
                <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-red-500 shrink-0"/> 届いたその日からすぐに楽しめる</li>
                <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-red-500 shrink-0"/> 家族で一緒に作る、かけがえのない時間</li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16 md:mb-20">
            <div className="relative group flex flex-col">
              <div className="w-full aspect-[4/3] rounded-[1.5rem] overflow-hidden border border-gray-200 bg-white">
                <img src="/image/8-1.png" alt="家族で塗っている様子" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
            </div>
            <div className="relative group flex flex-col">
              <div className="w-full aspect-[4/3] rounded-[1.5rem] overflow-hidden border border-gray-200 bg-white">
                <img src="/image/8-2.png" alt="スターターキット" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
            </div>
            <div className="relative group flex flex-col">
              <div className="w-full aspect-[4/3] rounded-[1.5rem] overflow-hidden border border-gray-200 bg-white">
                <img src="/image/8-3.png" alt="完成フィギュア" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
            </div>
          </div>

          {/* 色塗り体験ギミック */}
          <PaintExperience />
          
          <div id="ar-experience" className="bg-white border-2 border-red-100 rounded-[2rem] p-8 md:p-12 lg:p-16 text-gray-900 text-center relative overflow-hidden shadow-sm">
            <span className="bg-red-600 text-white px-4 py-1.5 rounded-full text-xs font-bold mb-6 inline-block tracking-widest uppercase shadow-sm">無料特典</span>
            <h2 className="text-2xl md:text-4xl font-black mb-6 tracking-tight leading-tight">
              届く前に、<br className="md:hidden"/>“うちの子”に会える。
            </h2>
            <p className="text-gray-700 text-sm md:text-base mb-10 font-bold">
              スマートフォンをかざすだけで、<br className="md:hidden"/>あなたの空間に“うちの子”が現れます。
            </p>

            <div className="max-w-3xl mx-auto mb-10">
              <div className="rounded-[1.5rem] overflow-hidden border border-gray-200 bg-gray-50 shadow-sm">
                <img src="/image/9-1.png" alt="AR体験（スマホ越しと実空間）" className="w-full h-auto object-cover" onError={(e)=>{e.target.src="https://placehold.co/1200x600"}} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 shadow-sm">
                <p className="text-sm text-gray-700 leading-relaxed font-bold">
                  本サービスでは、制作したフィギュアの3Dデータを活用し、AR（拡張現実）体験をお楽しみいただけます。<br/><br/>
                  ・お部屋の中に“うちの子”を表示<br/>
                  ・思い出の場所で再会<br/>
                  ・一緒に写真撮影<br/>
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-center">
                <p className="font-black text-base md:text-lg mb-2 text-red-600">決済完了後、AR閲覧用URLをすぐにお届けします。</p>
                <p className="text-sm text-gray-700 mb-4 font-bold">商品が届く前から、“うちの子”に会える体験をお楽しみいただけます。</p>
                <p className="text-xs text-gray-500">※AR体験の閲覧期限は【1ヶ月】となります。</p>
              </div>
            </div>

            {/* プレビューとARの期待値調整（細部指摘の防止） */}
            <div className="mt-8 bg-yellow-50 p-4 md:p-5 rounded-xl border border-yellow-200 text-left max-w-4xl mx-auto flex items-start gap-2.5 shadow-sm">
              <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
              <div className="text-xs md:text-sm text-yellow-900 font-bold leading-relaxed">
                ※事前の造形プレビュー画像およびAR表示は、あくまで全体の雰囲気を楽しんでいただくための「参考・体験用データ」です。<br className="hidden md:block"/>
                画面上で拡大して確認された場合、細部の形状や質感が実際の商品（出力された未塗装フィギュア）の見え方とは異なる場合がございます。あらかじめご了承ください。
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="flow" className="py-16 md:py-24 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-8 block uppercase">Flow</span>
          <img src="/image/10-1.png" alt="お届けまでのステップ" className="w-full h-auto rounded-[1.5rem] mx-auto border border-gray-200" onError={(e)=>{e.target.src="https://placehold.co/1200x800"}} />
        </div>
      </section>

      <section id="faq" className="py-16 md:py-24 bg-gray-50 border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4">
          <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block text-center uppercase">FAQ</span>
          <h2 className="text-2xl md:text-3xl font-black text-center text-gray-900 mb-10 tracking-tight">よくあるご質問</h2>
          
          <div className="bg-white rounded-[1.5rem] border border-gray-200 px-6 py-2 mb-8">
            <Accordion title="本当に似ますか？">全体の雰囲気やバランスをもとに、“その子らしさ”を再現する仕様となっております。細部の完全一致ではなく、全体として違和感がないかという観点でお楽しみいただく商品です。</Accordion>
            <Accordion title="確認後に修正はできますか？">AI生成の特性上、しっぽの形状・マズル・ポーズなど、細部の個別調整や大きな形状変更はできません。</Accordion>
            <Accordion title="完成イメージは事前に確認できますか？">はい、可能です。制作前に「造形イメージ（スクリーンショット）」をご確認いただき、承認後に本制作へ進みます。全体のフォルムや雰囲気をご確認いただく工程となります。</Accordion>
            <Accordion title="犬や猫以外の動物も制作できますか？">はい、可能です。うさぎや鳥、ハムスターなどの小動物から、爬虫類まで幅広く対応しております。ただし、対象の形状によっては難しい場合もございます。</Accordion>
          </div>
          
          <div className="text-center">
            <button onClick={() => navigateTo('faq-detail')} className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-8 py-3 rounded-full text-sm font-bold transition-colors text-gray-800">
              よくあるご質問をすべて見る
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </section>

      <section id="csr" className="py-16 md:py-20 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-3 tracking-tight">富士教材は盲導犬支援活動にも取り組んでいます</h2>
            <p className="text-gray-600 text-sm md:text-base">盲導犬など補助犬の記憶を形に残す活動を検討中</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-6">
            <div className="rounded-[1.5rem] overflow-hidden border border-gray-200 bg-gray-50">
              <img src="/image/12-1.png" alt="盲導犬" className="w-full h-40 md:h-48 object-cover" onError={(e)=>{e.target.src="https://placehold.co/400x300"}} />
            </div>
            <div className="rounded-[1.5rem] overflow-hidden border border-gray-200 bg-gray-50 relative">
              <img src="/image/12-3.png" alt="フィギュアに触れる様子" className="w-full h-40 md:h-48 object-cover" onError={(e)=>{e.target.src="https://placehold.co/400x300"}} />
            </div>
            <div className="rounded-[1.5rem] overflow-hidden border border-gray-200 bg-gray-50 relative">
              <img src="/image/12-2.png" alt="実際に出力したフィギュア" className="w-full h-40 md:h-48 object-cover" onError={(e)=>{e.target.src="https://placehold.co/400x300"}} />
              <span className="absolute bottom-3 right-3 bg-white/90 text-gray-800 text-xs font-bold px-3 py-1 rounded-full border border-gray-200">出力事例</span>
            </div>
          </div>
        </div>
      </section>

      <section id="b2b" className="py-16 md:py-24 bg-gray-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-gray-500 font-bold tracking-widest text-[10px] md:text-xs mb-3 block uppercase">Experience</span>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 tracking-tight">たとえば、こんな場所で。</h2>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              動物病院や水族館、いつものカフェでも。<br />
              「うちの子」ペイント体験は、<br className="md:hidden"/>様々な場所へ広がっています。
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {/* ① 動物病院 */}
            <div className="bg-white rounded-[1.5rem] overflow-hidden border border-gray-200 shadow-sm">
              <div className="aspect-[4/3] overflow-hidden bg-gray-50">
                <img src="/image/13-1.png" alt="動物病院" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
              <div className="p-5 text-center flex flex-col justify-center h-[100px]">
                <h3 className="font-black text-gray-900 mb-1 text-base">動物病院</h3>
                <p className="text-xs text-gray-500">待ち時間も、楽しい体験に</p>
              </div>
            </div>
            {/* ② 動物園ワークショップ */}
            <div className="bg-white rounded-[1.5rem] overflow-hidden border border-gray-200 shadow-sm">
              <div className="aspect-[4/3] overflow-hidden bg-gray-50">
                <img src="/image/13-2.png" alt="動物園" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
              <div className="p-5 text-center flex flex-col justify-center h-[100px]">
                <h3 className="font-black text-gray-900 mb-1 text-base">動物園</h3>
                <p className="text-xs text-gray-500">家族で楽しめる体験イベントに</p>
              </div>
            </div>
            {/* ③ 水族館・大型施設 */}
            <div className="bg-white rounded-[1.5rem] overflow-hidden border border-gray-200 shadow-sm">
              <div className="aspect-[4/3] overflow-hidden bg-gray-50">
                <img src="/image/13-3.png" alt="水族館・大型施設" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
              <div className="p-5 text-center flex flex-col justify-center h-[100px]">
                <h3 className="font-black text-gray-900 mb-1 text-base">水族館・大型施設</h3>
                <p className="text-xs text-gray-500">新しい体験コンテンツ</p>
              </div>
            </div>
            {/* ④ 猫カフェ・店舗 */}
            <div className="bg-white rounded-[1.5rem] overflow-hidden border border-gray-200 shadow-sm">
              <div className="aspect-[4/3] overflow-hidden bg-gray-50">
                <img src="/image/13-4.png" alt="猫カフェ・店舗" className="w-full h-full object-cover" onError={(e)=>{e.target.src="https://placehold.co/600x400"}} />
              </div>
              <div className="p-5 text-center flex flex-col justify-center h-[100px]">
                <h3 className="font-black text-gray-900 mb-1 text-base">猫カフェ・店舗</h3>
                <p className="text-xs text-gray-500">カフェ時間をもっと特別に</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button onClick={() => navigateTo('b2b')} className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-100 px-8 py-3 rounded-full text-sm font-bold transition-colors text-gray-800">
              法人導入をご検討の方はこちら
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </section>

      <div id="order-form">
        <OrderForm onReturnHome={navigateTo} />
      </div>
      
      <FloatingCTA targetId="order-form" onClick={()=>scrollToSection('order-form')} />
    </div>
  );
};

// ==========================================
// 注文フォームコンポーネント群（本番用）
// ==========================================
const OrderForm = ({ onReturnHome }) => {
  const [step, setStep] = useState(1);
  const [isSearchingZip, setIsSearchingZip] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const mainFileInputRef = useRef(null);
  const extraFileInputRef = useRef(null);
  
  // 初期値を定数として定義する
  const initialFormData = {
    lastName: '',        
    firstName: '',       
    lastNameKana: '',    
    firstNameKana: '',   
    email: '',
    phone: '',
    postalCode: '',
    prefecture: '',
    cityAddress: '',
    streetAddress: '', 
    buildingName: '',  
    petType: 'dog',
    qtyS: 0,
    qtyM: 1, 
    qtyL: 0,
    digitalOnly: false,
    includeDigitalData: false,
    includeGiftBox: false,
    mainPhoto: null,
    mainPhotoPreview: null,
    extraPhotos: [], 
    brushSetQty: 0,      
    fullPaintKitQty: 0,  
    qtyKeyring: 0,       
    qtyStrap: 0,         
    qtyWoodenBaseS: 0,
    qtyWoodenBaseM: 0,
    qtyWoodenBaseL: 0,
    engravingS: [''],
    engravingM: ['', ''],
    engravingL: ['', '', ''],
    proPaintS: 0,
    proPaintM: 0,
    proPaintL: 0,
    remarks: '',
    agreements: {
      isExperience: false,
      notExactMatch: false,
      noPoseChange: false,
      noRefundAfterPayment: false,
      noDataStorageRule: false,
      copyright: false,
      canCancelBySystem: false,
    }
  };

  // Stateの初期値に先ほどの定数をセット
  const [formData, setFormData] = useState(initialFormData);
  
  const prices = {
    s: { initial: 3800, add: 1800 },
    m: { initial: 6800, add: 3800 },
    l: { initial: 13800, add: 8800 },
    digital_only: 9800,
    data_addon: 6000,
    gift_box: 1000,
    shipping: 880,
    brush_set: 400,
    full_kit: 1100,
    keyring: 500,
    strap: 500,
    wooden_base_s: 2500,
    wooden_base_m: 4000,
    wooden_base_l: 6000,
    pro_paint: 10000,
  };
  
  const [totalPrice, setTotalPrice] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [lineTotals, setLineTotals] = useState({ s: 0, m: 0, l: 0 });
  
  const engravingLimits = {
    S: { lines: 1, half: 15, full: 7 },
    M: { lines: 2, half: 18, full: 9 },
    L: { lines: 3, half: 20, full: 10 }
  };
  
  const getValidation = (text, sizeKey) => {
    if (!text) return { isOver: false, limit: 0 };
    const isFullWidth = /[^\x01-\x7E]/.test(text);
    const limit = isFullWidth ? engravingLimits[sizeKey].full : engravingLimits[sizeKey].half;
    return { isOver: text.length > limit, limit };
  };

  useEffect(() => {
    let total = 0;
    let baseDiscount = 0;
    let newLineTotals = { s: 0, m: 0, l: 0 };

    if (formData.digitalOnly) {
      total = prices.digital_only;
    } else {
      const qties = { s: formData.qtyS, m: formData.qtyM, l: formData.qtyL };
      const totalUnits = qties.s + qties.m + qties.l;

      if (totalUnits > 0) {
        let initialSize = null;
        if (qties.l > 0) initialSize = 'l';
        else if (qties.m > 0) initialSize = 'm';
        else if (qties.s > 0) initialSize = 's';

        ['l', 'm', 's'].forEach(size => {
          const q = qties[size];
          if (q === 0) return;

          if (size === initialSize) {
            newLineTotals[size] = prices[size].initial + (q - 1) * prices[size].add;
            baseDiscount += (prices[size].initial - prices[size].add) * (q - 1);
          } else {
            newLineTotals[size] = q * prices[size].add;
            baseDiscount += (prices[size].initial - prices[size].add) * q;
          }
        });

        total = newLineTotals.s + newLineTotals.m + newLineTotals.l;
      }

      if (formData.includeDigitalData) total += prices.data_addon;
      if (formData.includeGiftBox) total += prices.gift_box;
      if (formData.brushSetQty > 0) total += prices.brush_set * formData.brushSetQty;
      if (formData.fullPaintKitQty > 0) total += prices.full_kit * formData.fullPaintKitQty;
      total += prices.keyring * formData.qtyKeyring;
      total += prices.strap * formData.qtyStrap;
      
      total += prices.wooden_base_s * formData.qtyWoodenBaseS;
      total += prices.wooden_base_m * formData.qtyWoodenBaseM;
      total += prices.wooden_base_l * formData.qtyWoodenBaseL;
      
      const totalProPaint = formData.proPaintS + formData.proPaintM + formData.proPaintL;
      total += prices.pro_paint * totalProPaint;
      if (totalUnits > 0) {
        total += prices.shipping;
      }
    }
    
    setTotalPrice(total);
    setDiscountAmount(baseDiscount);
    setLineTotals(newLineTotals);
  }, [formData]);
  
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const updateEngraving = (sizeKey, index, value) => {
    const field = `engraving${sizeKey}`;
    const newArr = [...formData[field]];
    newArr[index] = value;
    updateFormData(field, newArr);
  };

  const handleMainFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      updateFormData('mainPhoto', file);
      updateFormData('mainPhotoPreview', URL.createObjectURL(file));
    }
  };
  
  const handleExtraFilesChange = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    })).slice(0, 5 - formData.extraPhotos.length);
    setFormData(prev => ({
      ...prev,
      extraPhotos: [...prev.extraPhotos, ...newPhotos]
    }));
  };

  const removeExtraPhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      extraPhotos: prev.extraPhotos.filter((_, i) => i !== index)
    }));
  };

  const handleZipSearch = async () => {
    const code = formData.postalCode.replace('-', '');
    if (code.length !== 7) return;

    setIsSearchingZip(true);
    try {
      const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${code}`);
      const data = await response.json();
      
      if (data.status === 200 && data.results) {
        const result = data.results[0];
        updateFormData('prefecture', result.address1);
        updateFormData('cityAddress', result.address2 + result.address3);
      }
    } catch (error) {
      console.error("Postal code search failed", error);
    } finally {
      setIsSearchingZip(false);
    }
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // 画像ファイルとテキストデータを一緒に送るための「FormData」を作成
      const submitData = new FormData();
      
      // 1. テキストデータをすべて追加
      Object.keys(formData).forEach(key => {
        // 画像データは後で個別に追加するのでここではスキップ
        if (['mainPhoto', 'mainPhotoPreview', 'extraPhotos'].includes(key)) return;
        
        // 刻印テキスト（配列）や同意事項（オブジェクト）は文字に変換して格納
        if (typeof formData[key] === 'object' && formData[key] !== null) {
          submitData.append(key, JSON.stringify(formData[key]));
        } else {
          submitData.append(key, formData[key]);
        }
      });
      
      // 2. メイン写真を追加
      if (formData.mainPhoto) {
        submitData.append('mainPhoto', formData.mainPhoto);
      }
      
      // 3. 補足写真を追加（extraPhoto_0, extraPhoto_1... という名前で追加）
      formData.extraPhotos.forEach((photo, index) => {
        submitData.append(`extraPhoto_${index}`, photo.file);
      });

      // 4. 先ほどWordPressで作ったAPIのURLへ送信！
      const response = await fetch('https://uchinoko.fujikyouzai.com/wp-json/custom/v1/order', {
        method: 'POST',
        body: submitData, // 梱包したデータを乗せる
      });

      if (!response.ok) {
        throw new Error('サーバーエラーが発生しました');
      }
      
      // 送信成功したら完了画面を表示
      setIsSubmitted(true); 
      
    } catch (error) {
      console.error("Submit error:", error);
      alert('送信に失敗しました。通信環境をご確認の上、再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  // フォームをすべて初期状態に戻してトップへ戻る関数
  const handleResetAndReturn = () => {
    setFormData(initialFormData); // データを空に
    setStep(1);                   // STEP1に戻す
    setIsSubmitted(false);        // 完了画面フラグを下ろす
    setTotalPrice(0);
    setDiscountAmount(0);
    
    // extraPhotosのプレビューURLを解放（メモリリーク防止）
    formData.extraPhotos.forEach(photo => URL.revokeObjectURL(photo.preview));
    if(formData.mainPhotoPreview) URL.revokeObjectURL(formData.mainPhotoPreview);

    onReturnHome('home');         // トップへスクロール
  };
  
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const isPhoneValid = formData.phone.replace(/[^0-9]/g, '').length >= 10;
  const isPostalValid = formData.postalCode.replace(/[^0-9]/g, '').length === 7;
  const isKanaValid = (text) => /^[ァ-ンヴー]*$/.test(text) && text.length > 0;
  
  const isStep1Valid = 
    formData.lastName.trim().length >= 1 && 
    formData.firstName.trim().length >= 1 && 
    isKanaValid(formData.lastNameKana) && 
    isKanaValid(formData.firstNameKana) && 
    isEmailValid && 
    isPhoneValid && 
    isPostalValid && 
    formData.prefecture.trim() && 
    formData.cityAddress.trim() && 
    formData.streetAddress.trim();
    
  const isStep2Valid = formData.mainPhoto && (formData.digitalOnly || (formData.qtyS + formData.qtyM + formData.qtyL > 0));
  
  const isEngravingOver = (
    (formData.qtyWoodenBaseS > 0 && formData.engravingS.some((t, i) => getValidation(t, 'S').isOver)) ||
    (formData.qtyWoodenBaseM > 0 && formData.engravingM.some((t, i) => getValidation(t, 'M').isOver)) ||
    (formData.qtyWoodenBaseL > 0 && formData.engravingL.some((t, i) => getValidation(t, 'L').isOver))
  );
  
  const isStep3Valid = !isEngravingOver;
  const isAllAgreed = Object.values(formData.agreements).every(v => v === true);
  const totalPhysicalQty = formData.qtyS + formData.qtyM + formData.qtyL;
  const totalProPaintQty = formData.proPaintS + formData.proPaintM + formData.proPaintL;

  const getExperienceAgreementText = () => {
    if (formData.digitalOnly) return "本注文は実物のフィギュアを含まない【デジタルデータのみ】の購入であることを理解しました";
    if (totalProPaintQty === 0) return "本品は【未塗装のフィギュア】が届き、ご自身の手で完成させる「体験型キット」であることを理解しました";
    if (totalProPaintQty >= totalPhysicalQty && totalPhysicalQty > 0) return "【プロ塗装オプション】により、職人が彩色を施した完成品が届くオーダーメイド品であることを理解しました";
    return `選択したプロ塗装分は彩色済み、それ以外は【未塗装のフィギュア】として届くことを理解しました`;
  };
  
  function updateAgreement(field) {
    setFormData(prev => ({
      ...prev,
      agreements: { ...prev.agreements, [field]: !prev.agreements[field] }
    }));
  }

  if (isSubmitted) {
    return (
      <div className="bg-[#FFF8F0] flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-700 rounded-3xl">
        <div className="max-w-2xl w-full bg-white rounded-[3.5rem] shadow-2xl overflow-hidden border border-orange-100 flex flex-col items-center">
          
          <div className="w-full bg-[#3E2723] py-12 px-8 flex flex-col items-center">
            <div className="w-20 h-20 bg-[#D4A373] rounded-full flex items-center justify-center mb-6 shadow-lg">
              <CheckCircle2 size={40} className="text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-white text-center tracking-tight leading-tight mb-3">制作依頼を送信しました</h2>
            <p className="text-orange-100 text-sm md:text-base font-bold text-center opacity-90">
              ご依頼ありがとうございます。<br className="hidden md:block" />
              これより3Dデータの制作準備を開始いたします。
            </p>
          </div>

          <div className="p-8 md:p-12 w-full space-y-10">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="bg-[#D4A373] text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">Next Step</div>
                <div className="h-px bg-orange-100 flex-1"></div>
              </div>
              <div className="space-y-3">
                <p className="text-[#3E2723] font-bold text-sm leading-relaxed pl-2 border-l-4 border-orange-200">
                  通常3〜5営業日以内を目安に、造形イメージのスクリーンショットをメールにてお送りします。
                </p>
                <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                  <p className="text-[10px] text-gray-500 font-bold leading-relaxed italic">
                    ※混雑状況により上記目安よりお時間をいただく場合がございます。また、お送りいただいた写真での生成が困難な場合は、再提出をお願いすることがあります。
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="bg-orange-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">特別な召喚特典</div>
                <div className="h-px bg-orange-100 flex-1"></div>
              </div>
              <div className="bg-orange-50/50 p-6 rounded-[2.5rem] border border-orange-100 relative overflow-hidden group">
                <div className="relative z-10 flex gap-4 items-start">
                  <div className="bg-white p-3 rounded-2xl shadow-sm text-orange-500">
                    <Sparkles size={24} className="animate-pulse" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-[#3E2723] font-black text-sm leading-relaxed">
                      承認・決済が完了しましたら、スマホで会える「ARうちの子」のリンクを順次お届けします！
                    </p>
                    <p className="text-[10px] text-orange-600 font-bold italic">あの子と一緒に過ごせる魔法の体験をお楽しみに。</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-orange-200/50">
                  <p className="text-[10px] text-gray-400 font-bold leading-relaxed">
                    ※イメージをご確認いただき、承認・決済が完了した後に本制作（プリント）へと進みます。
                  </p>
                </div>
              </div>
            </div>

            {/* ★ onClickをリセット関数に変更 */}
            <button 
              onClick={handleResetAndReturn} 
              className="w-full bg-[#3E2723] text-white py-5 rounded-full font-black text-sm shadow-xl hover:bg-black transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              トップページへ
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="w-full bg-gray-50/50 py-4 text-center border-t border-gray-50">
             <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Fujikyouzai Pet Figure Service</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FFF8F0] text-[#3E2723] font-sans pb-20 rounded-t-[3rem] overflow-hidden shadow-2xl mt-12">
      <div className="bg-[#3E2723] text-white py-12 px-6 text-center shadow-lg border-b-4 border-[#D4A373]">
        <h2 className="text-3xl font-bold mb-4 tracking-tight text-white">制作依頼・ご注文フォーム</h2>
        <div className="max-w-2xl mx-auto bg-white/10 p-6 rounded-3xl text-sm leading-relaxed border border-white/20 backdrop-blur-sm">
          <p className="flex items-center justify-center gap-2 font-bold text-orange-200 mb-2 text-base">
            <ShieldCheck size={22} className="text-[#D4A373]" /> 独自の「安心プレビュー」採用
          </p>
          <p className="opacity-90 leading-relaxed">
            送信後、3Dデータを制作し、<span className="font-bold text-white underline decoration-[#D4A373]">「造形イメージのスクリーンショット」</span>をメール送付します。<br/>
            内容をご承認いただいてから決済・本制作となるため、完成イメージがわかって安心です。
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto -mt-10 px-4">
        <div className="bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 border border-orange-100 relative text-[#3E2723]">
          <div className="flex justify-between mb-16 max-w-sm mx-auto relative">
             <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -z-0 -translate-y-1/2"></div>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm z-10 transition-all duration-300 ${step >= i ? 'bg-[#D4A373] text-white scale-110 shadow-lg' : 'bg-white text-gray-300 border-2 border-gray-100'}`}>
                {i}
              </div>
            ))}
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in duration-500">
              <SectionTitle icon={<User />} title="① お客様・お届け先情報" />
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input label="姓（必須）" value={formData.lastName} onChange={v => updateFormData('lastName', v)} placeholder="富士" />
                  <Input label="名（必須）" value={formData.firstName} onChange={v => updateFormData('firstName', v)} placeholder="太郎" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="セイ（フリガナ）" value={formData.lastNameKana} onChange={v => updateFormData('lastNameKana', v)} placeholder="フジ" error={formData.lastNameKana && !isKanaValid(formData.lastNameKana)} />
                  <Input label="メイ（フリガナ）" value={formData.firstNameKana} onChange={v => updateFormData('firstNameKana', v)} placeholder="タロウ" error={formData.firstNameKana && !isKanaValid(formData.firstNameKana)} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="メールアドレス" value={formData.email} onChange={v => updateFormData('email', v)} type="email" placeholder="example@mail.com" error={formData.email && !isEmailValid} />
                <Input label="電話番号" value={formData.phone} onChange={v => updateFormData('phone', v)} type="tel" placeholder="090-0000-0000" error={formData.phone && !isPhoneValid} />
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-gray-400 ml-2 uppercase tracking-wider">郵便番号</label>
                  <div className="flex gap-2">
                    <input type="text" value={formData.postalCode} onChange={e => updateFormData('postalCode', e.target.value)} placeholder="000-0000" className={`flex-1 p-4 bg-gray-50 border rounded-2xl text-sm outline-none text-[#3E2723] focus:ring-2 focus:ring-[#D4A373] ${formData.postalCode && !isPostalValid ? 'border-red-300 bg-red-50' : 'border-gray-100'}`} />
                    <button onClick={handleZipSearch} disabled={isSearchingZip || formData.postalCode.length < 7} className="bg-[#3E2723] text-white px-4 rounded-2xl flex items-center gap-1 text-xs font-bold hover:bg-black transition-colors disabled:bg-gray-200">
                      {isSearchingZip ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} 住所検索
                    </button>
                  </div>
                </div>
                <Input label="都道府県" value={formData.prefecture} onChange={v => updateFormData('prefecture', v)} placeholder="〇〇県（検索で自動入力）" />
                <Input label="市区町村" value={formData.cityAddress} onChange={v => updateFormData('cityAddress', v)} placeholder="〇〇市〇〇町（検索で自動入力）" />
                <Input label="番地" value={formData.streetAddress} onChange={v => updateFormData('streetAddress', v)} placeholder="1丁目2-3" />
                <Input label="建物名・号室（任意）" value={formData.buildingName} onChange={v => updateFormData('buildingName', v)} placeholder="富士ビル 101" />
              </div>
              <SectionTitle icon={<Dog />} title="② ペットのしゅるい" />
              <div className="flex gap-4">
                {['dog', 'cat', 'other'].map(type => (
                  <button key={type} onClick={() => updateFormData('petType', type)} className={`flex-1 py-4 rounded-2xl border-2 font-bold transition-all ${formData.petType === type ? 'border-[#D4A373] bg-orange-50 text-[#D4A373]' : 'border-gray-100 text-gray-400'}`}>
                    {type === 'dog' ? 'いぬ' : type === 'cat' ? 'ねこ' : 'その他'}
                  </button>
                ))}
              </div>
              <button onClick={nextStep} disabled={!isStep1Valid} className="w-full bg-[#3E2723] text-white py-5 rounded-full font-bold mt-8 flex items-center justify-center gap-2 disabled:bg-gray-200 shadow-xl transition-all active:scale-95 cursor-pointer">
                {!isStep1Valid && (formData.email || formData.phone || formData.lastName) ? '入力内容を確認してください' : 'サイズ・数量選択へ'} <ChevronRight size={18} />
              </button>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-8 animate-in slide-in-from-right duration-500">
              <SectionTitle icon={<ImageIcon />} title="③ 写真の選択 ＆ 制作数" />
              
              <div className="bg-orange-50/50 rounded-3xl p-6 border border-orange-100">
                <div className="flex items-center gap-2 mb-4 text-[#D4A373] font-bold text-sm">
                  <HelpCircle size={18} /> きれいに作るための撮影のコツ
                </div>
                <div className="space-y-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 text-xs font-bold text-green-700">
                      <CheckCircle size={16} className="shrink-0" /> 明るい場所（自然光）で撮影
                    </div>
                    <div className="flex items-center gap-3 text-xs font-bold text-green-700">
                      <CheckCircle size={16} className="shrink-0" /> 全身（耳から尻尾まで）が写っている
                    </div>
                    <div className="flex items-center gap-3 text-xs font-bold text-green-700">
                      <CheckCircle size={16} className="shrink-0" /> ペットと同じ目線（高さ）で撮影
                    </div>
                  </div>
                  <div className="h-px bg-orange-200 my-2"></div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 text-xs font-bold text-red-600">
                      <AlertCircle size={16} className="shrink-0" /> 逆光・暗すぎる場所はNG
                    </div>
                    <div className="flex items-center gap-3 text-xs font-bold text-red-600">
                      <AlertCircle size={16} className="shrink-0" /> 体の一部が隠れている写真はNG
                    </div>
                    <div className="flex items-center gap-3 text-xs font-bold text-red-600">
                      <AlertCircle size={16} className="shrink-0" /> ピンぼけ・激しく動いている写真はNG
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold mb-3 border-l-4 border-[#D4A373] pl-2">メインの写真（必須）</label>
                  <div onClick={() => mainFileInputRef.current?.click()} className={`border-4 border-dashed rounded-[2.5rem] p-8 text-center transition-all cursor-pointer group ${formData.mainPhotoPreview ? 'border-green-400 bg-green-50' : 'border-gray-100 bg-gray-50 hover:border-[#D4A373] hover:bg-orange-50'}`}>
                    <input type="file" ref={mainFileInputRef} onChange={handleMainFileChange} accept="image/*" className="hidden" />
                    {formData.mainPhotoPreview ? (
                      <div className="flex flex-col items-center">
                        <img src={formData.mainPhotoPreview} alt="preview" className="w-32 h-32 object-cover rounded-2xl mb-4 shadow-md border-4 border-white" />
                        <p className="font-bold text-green-700">メイン写真をセットしました</p>
                        <button onClick={(e) => {e.stopPropagation(); updateFormData('mainPhoto', null); updateFormData('mainPhotoPreview', null);}} className="mt-4 text-[10px] font-bold text-red-400 hover:text-red-600 flex items-center gap-1"><X size={12}/> 写真を変更する</button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="bg-white p-5 rounded-full mb-4 shadow-sm group-hover:scale-110 transition-transform"><Upload className="text-[#D4A373]" size={36} /></div>
                        <p className="font-bold text-lg">ポーズの基準となる写真を選択</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-1 border-l-4 border-[#D4A373] pl-2">補足の写真（任意 / 最大5枚）</label>
                  <p className="text-[11px] text-[#D4A373] font-bold mb-3 ml-2 italic">※正面・背面・左右・天面など、多角的な写真をいただけると造形精度が向上します。</p>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {formData.extraPhotos.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-2xl overflow-hidden group shadow-sm border border-gray-100">
                        <img src={photo.preview} alt={`extra-${index}`} className="w-full h-full object-cover" />
                        <button onClick={() => removeExtraPhoto(index)} className="absolute top-2 right-2 bg-black/50 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
                      </div>
                    ))}
                    {formData.extraPhotos.length < 5 && (
                      <div onClick={() => extraFileInputRef.current?.click()} className="aspect-square border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center bg-gray-50 cursor-pointer hover:border-[#D4A373] hover:bg-orange-50 transition-all">
                        <Plus size={24} className="text-gray-300" />
                        <span className="text-[9px] font-bold text-gray-400 mt-1 uppercase">ADD PHOTO</span>
                        <input type="file" ref={extraFileInputRef} onChange={handleExtraFilesChange} accept="image/*" multiple className="hidden" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-end mb-2">
                  <div className="space-y-1">
                    <p className="font-bold text-sm border-l-4 border-[#D4A373] pl-2">サイズと数量</p>
                    <p className="text-[10px] text-gray-400 ml-2 italic">※複数サイズ選択時は、最も大きいサイズが「基本制作費」の対象となります。</p>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-bold cursor-pointer text-gray-500">
                    <input type="checkbox" checked={formData.digitalOnly} onChange={e => updateFormData('digitalOnly', e.target.checked)} className="accent-[#D4A373]" />
                    データ単体購入のみ
                  </label>
                </div>

                {!formData.digitalOnly && (
                  <div className="bg-orange-50/50 border border-orange-200 p-4 rounded-2xl mb-4 flex items-center gap-3 animate-in slide-in-from-top-2 duration-700">
                    <Sparkles size={20} className="text-orange-500 shrink-0" />
                    <p className="text-[11px] md:text-xs font-black text-orange-800 leading-relaxed">1体目のデータがある今だけ、2体目以降やオプションがお得に追加できます。</p>
                  </div>
                )}

                {!formData.digitalOnly ? (
                  <div className="space-y-3">
                    <QtySelector label="S：ポケット (約3.5cm)" initial={prices.s.initial} add={prices.s.add} qty={formData.qtyS} lineTotal={lineTotals.s} onChange={v => updateFormData('qtyS', v)} isFirst={totalPhysicalQty > 0 && ((formData.qtyL === 0 && formData.qtyM === 0) || (formData.qtyS > 0 && formData.qtyL === 0 && formData.qtyM === 0))} />
                    <QtySelector label="M：標準 (約6.5cm)" initial={prices.m.initial} add={prices.m.add} qty={formData.qtyM} lineTotal={lineTotals.m} onChange={v => updateFormData('qtyM', v)} isFirst={totalPhysicalQty > 0 && (formData.qtyL === 0 && formData.qtyM > 0)} />
                    <QtySelector label="L：メモリアル (約10cm)" initial={prices.l.initial} add={prices.l.add} qty={formData.qtyL} lineTotal={lineTotals.l} onChange={v => updateFormData('qtyL', v)} isFirst={totalPhysicalQty > 0 && formData.qtyL > 0} />
                    
                    {totalPhysicalQty > 1 && (
                      <div className="bg-green-50 p-6 rounded-[2rem] flex flex-col md:flex-row items-center justify-between text-green-700 border border-green-100 shadow-sm animate-in zoom-in duration-300 gap-4 text-right">
                        <div className="flex items-center gap-3 text-left">
                          <Heart size={20} fill="currentColor" className="text-green-500 animate-pulse" />
                          <div>
                            <span className="text-xs font-black block text-green-800 tracking-tighter">おまとめ特典適用中！</span>
                            <span className="text-[10px] opacity-70">最大の1体を基準とし、追加分は造形費のみでお得に。</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <div><span className="text-[10px] opacity-70 block text-gray-400 font-bold uppercase tracking-widest">Saved / 割引額</span><span className="font-black text-lg">-¥{discountAmount.toLocaleString()}</span></div>
                          <div><span className="text-[10px] opacity-70 block text-gray-500 font-bold tracking-tighter">Est. Total / 合計</span><span className="font-black text-2xl text-[#D4A373]">¥{totalPrice.toLocaleString()}</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-8 rounded-3xl border-2 border-[#D4A373] bg-orange-50 flex justify-between items-center shadow-inner">
                    <div className="text-[#3E2723]"><p className="font-bold text-lg">デジタルデータ(GLB)単体</p><p className="text-xs text-gray-500">VR/メタバース用・3Dプリント出力用データ</p></div>
                    <span className="text-2xl font-black text-[#D4A373]">¥{prices.digital_only.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <button onClick={prevStep} className="flex-1 border-2 border-gray-100 py-4 rounded-full font-bold flex items-center justify-center gap-2 hover:bg-white transition-colors text-gray-400 cursor-pointer"><ChevronLeft size={18} /> 戻る</button>
                <button onClick={nextStep} disabled={!isStep2Valid} className="flex-[2] bg-[#3E2723] text-white py-4 rounded-full font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-black transition-colors disabled:bg-gray-200 cursor-pointer">オプション選択へ <ChevronRight size={18} /></button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-8 animate-in slide-in-from-right duration-500">
              <SectionTitle icon={<Package />} title="④ オプション選択" />
              <div className="space-y-6">
                
                <div className="bg-orange-50/40 p-6 rounded-[2rem] border-2 border-orange-100 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 bg-orange-100 px-4 py-1 rounded-bl-2xl text-[10px] font-black text-orange-600 tracking-widest uppercase">Special Asset</div>
                  <CheckboxItem 
                    icon={<Smartphone size={18} />} 
                    label="デジタルデータ(GLB)追加" 
                    price={prices.data_addon} 
                    checked={formData.includeDigitalData} 
                    onChange={v => updateFormData('includeDigitalData', v)} 
                    disabled={formData.digitalOnly} 
                    subtext="【おすすめ】「うちの子」の3Dデータを一生の資産として手元に残せます。" 
                  />
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 opacity-90">
                    <div className={`p-3 rounded-xl flex items-start gap-2 border transition-colors ${formData.includeDigitalData ? 'bg-white border-orange-200 shadow-sm' : 'bg-gray-100/50 border-gray-200'}`}>
                      <Sparkles size={14} className="text-orange-400 mt-0.5" />
                      <p className="text-[10px] text-gray-500 leading-relaxed font-bold">VRChatやclusterなどのメタバース空間で、うちの子のアバターとして一緒に歩けます。</p>
                    </div>
                    <div className={`p-3 rounded-xl flex items-start gap-2 border transition-colors ${formData.includeDigitalData ? 'bg-white border-orange-200 shadow-sm' : 'bg-gray-100/50 border-gray-200'}`}>
                      <ShieldCheck size={14} className="text-orange-400 mt-0.5" />
                      <p className="text-[10px] text-gray-500 leading-relaxed font-bold">万が一フィギュアが破損しても、データがあれば何度でも再製作が可能です。</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-3xl space-y-4 border border-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Hobby & Kits</h3>
                    <div className="bg-white px-3 py-1 rounded-full border border-orange-100 shadow-sm flex items-center gap-1"><Info size={12} className="text-[#D4A373]" /><span className="text-[10px] font-bold text-[#D4A373]">1セット分は無料で付属しています</span></div>
                  </div>
                  <div className="space-y-3">
                    <SimpleQtySelector icon={<Brush size={18} />} label="高品質筆3本セット" price={prices.brush_set} qty={formData.brushSetQty} onChange={v => updateFormData('brushSetQty', v)} subtext="予備や、ご家族で同時に塗る場合に追加してください。" />
                    <SimpleQtySelector icon={<Palette size={18} />} label="フル塗装キット (筆+塗料3～4色)" price={prices.full_kit} qty={formData.fullPaintKitQty} onChange={v => updateFormData('fullPaintKitQty', v)} subtext="ご家族やご友人と複数名で制作を楽しみたい場合に追加してください。" />
                  </div>
                </div>

                {totalPhysicalQty > 0 && (
                  <div className="bg-gray-50 p-5 rounded-2xl space-y-4 border border-gray-100 text-[#3E2723]">
                    <div className="flex justify-between items-center text-right">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest text-left">プロ塗装オプション</h3>
                      <div className="bg-orange-100 text-[#D4A373] px-3 py-1 rounded-full text-[10px] font-bold">10,000円 / 体</div>
                    </div>
                    <div className="space-y-3">
                      <p className="text-[11px] text-[#3E2723] font-black mb-1 flex items-center gap-1"><Sparkles size={14} className="text-orange-400" /> 模型コンテスト全国最優秀賞モデラーが彩色を監修。</p>
                      <p className="text-[10px] text-gray-500 mb-2 border-l-2 border-orange-200 pl-2">どのサイズをプロ塗装しますか？</p>
                      <p className="text-[9px] text-red-500 font-bold mb-3 ml-2 italic">※プロ塗装オプションを選択された個体には、筆・塗料セットは付属しません。</p>
                      {formData.qtyS > 0 && <SubQtySelector label="Sサイズをプロ塗装" max={formData.qtyS} qty={formData.proPaintS} onChange={v => updateFormData('proPaintS', v)} />}
                      {formData.qtyM > 0 && <SubQtySelector label="Mサイズをプロ塗装" max={formData.qtyM} qty={formData.proPaintM} onChange={v => updateFormData('proPaintM', v)} />}
                      {formData.qtyL > 0 && <SubQtySelector label="Lサイズをプロ塗装" max={formData.qtyL} qty={formData.proPaintL} onChange={v => updateFormData('proPaintL', v)} />}
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 p-5 rounded-2xl space-y-3 border border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-2 gap-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest text-left">Accessories</h3>
                    <p className="text-[9px] text-[#D4A373] font-bold italic">※穴あけ位置のご指定がない場合は当社にてバランスを見て加工いたします。</p>
                  </div>
                  <SimpleQtySelector icon={<Key size={18} />} label="キーホルダー金具" price={prices.keyring} qty={formData.qtyKeyring} onChange={v => updateFormData('qtyKeyring', v)} subtext="お出かけ用に。" />
                  <SimpleQtySelector icon={<Tag size={18} />} label="ストラップ紐" price={prices.strap} qty={formData.qtyStrap} onChange={v => updateFormData('qtyStrap', v)} subtext="スマホやバッグに。" />
                  
                  <div className="pt-4 border-t border-gray-200 space-y-6">
                    <div className="space-y-1">
                      <h4 className="text-[10px] font-bold text-gray-400 flex items-center gap-1 uppercase tracking-wider"><Layout size={12}/> 木製台座オプション</h4>
                      <p className="text-[10px] text-[#D4A373] font-bold ml-4 italic">刻印入りの専用台座で、メモリアルな風格が宿ります。</p>
                    </div>
                    
                    <div className="space-y-4">
                      <SimpleQtySelector icon={<Layout size={18} />} label="木製台座 (Sサイズ用)" price={prices.wooden_base_s} qty={formData.qtyWoodenBaseS} onChange={v => updateFormData('qtyWoodenBaseS', v)} subtext="中央に1行の刻印が可能です。" />
                      {formData.qtyWoodenBaseS > 0 && (
                        <div className="bg-white p-4 rounded-2xl border border-orange-100 shadow-inner space-y-3 animate-in fade-in zoom-in duration-300">
                          <EngravingInput sizeKey="S" index={0} value={formData.engravingS[0]} onChange={(v) => updateEngraving('S', 0, v)} validation={getValidation(formData.engravingS[0], 'S')} />
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <SimpleQtySelector icon={<Layout size={18} />} label="木製台座 (Mサイズ用)" price={prices.wooden_base_m} qty={formData.qtyWoodenBaseM} onChange={v => updateFormData('qtyWoodenBaseM', v)} subtext="2行の刻印が可能です（2行目は任意）。" />
                      {formData.qtyWoodenBaseM > 0 && (
                        <div className="bg-white p-4 rounded-2xl border border-orange-100 shadow-inner space-y-3 animate-in fade-in zoom-in duration-300">
                          <EngravingInput sizeKey="M" index={0} value={formData.engravingM[0]} onChange={(v) => updateEngraving('M', 0, v)} validation={getValidation(formData.engravingM[0], 'M')} />
                          <EngravingInput sizeKey="M" index={1} value={formData.engravingM[1]} onChange={(v) => updateEngraving('M', 1, v)} validation={getValidation(formData.engravingM[1], 'M')} />
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <SimpleQtySelector icon={<Layout size={18} />} label="木製台座 (Lサイズ用)" price={prices.wooden_base_l} qty={formData.qtyWoodenBaseL} onChange={v => updateFormData('qtyWoodenBaseL', v)} subtext="最大3行の刻印が可能です（2-3行目は任意）。" />
                      {formData.qtyWoodenBaseL > 0 && (
                        <div className="bg-white p-4 rounded-2xl border border-orange-100 shadow-inner space-y-3 animate-in fade-in zoom-in duration-300">
                          <EngravingInput sizeKey="L" index={0} value={formData.engravingL[0]} onChange={(v) => updateEngraving('L', 0, v)} validation={getValidation(formData.engravingL[0], 'L')} />
                          <EngravingInput sizeKey="L" index={1} value={formData.engravingL[1]} onChange={(v) => updateEngraving('L', 1, v)} validation={getValidation(formData.engravingL[1], 'L')} />
                          <EngravingInput sizeKey="L" index={2} value={formData.engravingL[2]} onChange={(v) => updateEngraving('L', 2, v)} validation={getValidation(formData.engravingL[2], 'L')} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50/40 p-6 rounded-[2rem] border-2 border-orange-100">
                  <CheckboxItem 
                    icon={<Gift size={18} />} 
                    label="想い出を贈るメモリアルギフトBOXを追加する" 
                    price={prices.gift_box} 
                    checked={formData.includeGiftBox} 
                    onChange={v => updateFormData('includeGiftBox', v)} 
                    disabled={formData.digitalOnly} 
                    subtext="大切な方への贈り物として、そのままお渡しできる特別仕様です。" 
                  />
                </div>

              </div>

              <div className="space-y-2">
                <label className="block text-sm font-bold flex items-center gap-2"><Palette size={16} className="text-[#D4A373]" /> ⑤ 備考欄</label>
                <textarea value={formData.remarks} onChange={e => updateFormData('remarks', e.target.value)} placeholder="ご要望・気になる点をご記入ください" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl h-24 text-sm outline-none focus:ring-2 focus:ring-orange-200 shadow-inner text-[#3E2723]"></textarea>
              </div>

              <div className="flex gap-4">
                <button onClick={prevStep} className="flex-1 border-2 border-gray-100 py-4 rounded-full font-bold flex items-center justify-center gap-2 hover:bg-white transition-colors text-gray-400 text-left cursor-pointer"><ChevronLeft size={18} /> 戻る</button>
                <button onClick={nextStep} disabled={!isStep3Valid} className="flex-[2] bg-[#3E2723] text-white py-4 rounded-full font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-black transition-colors disabled:bg-gray-200 cursor-pointer">
                  {!isStep3Valid ? '刻印文字数を確認してください' : '最終確認へ'} <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="space-y-8 animate-in slide-in-from-right duration-500">
              <SectionTitle icon={<ShieldCheck />} title="⑥ 最終確認・同意" />
              <div className="bg-gray-50 p-8 rounded-[2rem] border border-gray-200 shadow-inner">
                <div className="flex justify-between items-center border-b pb-4 mb-4 text-right">
                  <h3 className="font-bold text-xs text-gray-400 tracking-widest uppercase flex items-center gap-2 text-left"><Info size={14} /> お見積り明細 (税込概算)</h3>
                </div>
                <div className="space-y-2 mb-6">
                  {formData.digitalOnly ? <SummaryRow label="デジタルデータ単体" price={prices.digital_only} /> : (
                    <>
                      {formData.qtyS > 0 && <SummaryRow label={`Sサイズ × ${formData.qtyS}`} price={lineTotals.s} />} 
                      {formData.qtyM > 0 && <SummaryRow label={`Mサイズ × ${formData.qtyM}`} price={lineTotals.m} />} 
                      {formData.qtyL > 0 && <SummaryRow label={`Lサイズ × ${formData.qtyL}`} price={lineTotals.l} />} 
                      <SummaryRow label="梱包送料" price={prices.shipping} highlight />
                    </>
                  )}
                  {formData.includeDigitalData && <SummaryRow label="データ(GLB)追加" price={prices.data_addon} highlight />}
                  {formData.includeGiftBox && <SummaryRow label="メモリアルギフトBOX" price={prices.gift_box} highlight />}
                  {formData.brushSetQty > 0 && <SummaryRow label={`高品質筆3本セット × ${formData.brushSetQty}`} price={prices.brush_set * formData.brushSetQty} />}
                  {formData.fullPaintKitQty > 0 && <SummaryRow label={`フル塗装キット × ${formData.fullPaintKitQty}`} price={prices.full_kit * formData.fullPaintKitQty} />}
                  {totalProPaintQty > 0 && <SummaryRow label={`プロ塗装(合計 ${totalProPaintQty}体)`} price={prices.pro_paint * totalProPaintQty} highlight />}
                  {formData.qtyKeyring > 0 && <SummaryRow label={`キーホルダー金具 × ${formData.qtyKeyring}`} price={prices.keyring * formData.qtyKeyring} />}
                  {formData.qtyStrap > 0 && <SummaryRow label={`ストラップ紐 × ${formData.qtyStrap}`} price={prices.strap * formData.qtyStrap} />}
                  {formData.qtyWoodenBaseS > 0 && <SummaryRow label={`木製台座(S) × ${formData.qtyWoodenBaseS}`} price={prices.wooden_base_s * formData.qtyWoodenBaseS} />}
                  {formData.qtyWoodenBaseM > 0 && <SummaryRow label={`木製台座(M) × ${formData.qtyWoodenBaseM}`} price={prices.wooden_base_m * formData.qtyWoodenBaseM} />}
                  {formData.qtyWoodenBaseL > 0 && <SummaryRow label={`木製台座(L) × ${formData.qtyWoodenBaseL}`} price={prices.wooden_base_l * formData.qtyWoodenBaseL} />}
                </div>
                <div className="border-t-2 border-dashed border-gray-300 pt-6 flex justify-between items-center text-[#3E2723] text-right">
                  <div className="space-y-1 text-left">
                    <span className="font-black text-lg block tracking-tighter text-gray-400 uppercase">Total Estimate / 合計予定金額</span>
                    <p className="text-[10px] text-gray-500 font-bold leading-relaxed">
                      ※沖縄・離島への配送は、通常送料とは別に追加送料が発生いたします。<br />
                      追加送料につきましては、決済時に加算されます。
                    </p>
                  </div>
                  <div><span className="text-4xl font-black text-[#D4A373]">¥{totalPrice.toLocaleString()}</span></div>
                </div>
              </div>

              <div className="p-6 bg-red-50/50 rounded-[2.5rem] border-2 border-red-100 space-y-3 shadow-inner">
                <div className="flex items-center gap-2 text-red-800 font-black mb-4 justify-center italic text-lg leading-none"><ShieldAlert size={24} /> 同意事項の確認</div>
                <div className="space-y-1">
                  <AgreementItem checked={formData.agreements.isExperience} onChange={() => updateAgreement('isExperience')} text={getExperienceAgreementText()} />
                  <AgreementItem checked={formData.agreements.notExactMatch} onChange={() => updateAgreement('notExactMatch')} text="1枚の写真からAIが推測して造形するため、実物と細部が完全一致しない場合があることを理解しました" />
                  <AgreementItem checked={formData.agreements.noPoseChange} onChange={() => updateAgreement('noPoseChange')} text="AI生成の特性上、データの完成後にポーズの変更や修正を行うことは一切できません。あらかじめご了承ください" />
                  <AgreementItem checked={formData.agreements.canCancelBySystem} onChange={() => updateAgreement('canCancelBySystem')} text="AI生成のため、お送りいただいた画像の状態によっては生成が困難な場合があります。その際は注文をキャンセルさせていただくか、再送付をお願いする場合があることに同意します" important />
                  <AgreementItem checked={formData.agreements.noRefundAfterPayment} onChange={() => updateAgreement('noRefundAfterPayment')} text="完全オーダーメイド品のため、決済完了後（制作開始後）の返品・返金はできないことに同意します" />
                  <AgreementItem checked={formData.agreements.noDataStorageRule} onChange={() => updateAgreement('noDataStorageRule')} text="【3Dデータは保管いたしません】。注文完了後にデータを削除するため、後日の「再注文」の際は再度データ制作が必要となることに同意します" important />
                  <AgreementItem checked={formData.agreements.copyright} onChange={() => updateAgreement('copyright')} text="提供する写真は自身が撮影したもの、または権利者から許可を得たものであり、著作権を侵害していないことを保証します" />
                </div>
              </div>

              <div className="flex gap-4">
                <button onClick={prevStep} className="flex-1 border-2 border-gray-100 py-5 rounded-full font-bold flex items-center justify-center gap-2 hover:bg-white transition-colors text-gray-400 cursor-pointer">
                  <ChevronLeft size={18} /> 戻る
                </button>
                <button 
                  onClick={handleSubmit}
                  disabled={!isAllAgreed || isSubmitting} 
                  className={`flex-[2] py-5 rounded-full font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${isAllAgreed && !isSubmitting ? 'bg-[#D4A373] text-white shadow-xl scale-105 active:scale-95' : 'bg-gray-200 text-gray-400'}`}
                >
                  {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> 送信中...</> : <>依頼を送信して3D制作を開始する <ChevronRight size={18} /></>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---

function EngravingInput({ sizeKey, index, value, onChange, validation }) {
  const { isOver, limit } = validation;
  const limitsText = sizeKey === 'S' ? "全角7文字 / 半角15文字まで" : sizeKey === 'M' ? "全角9文字 / 半角18文字まで" : "全角10文字 / 半角20文字まで";
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center px-2">
        <label className="text-[10px] font-bold text-gray-400 italic">{index + 1}行目</label>
        <span className={`text-[9px] font-bold ${isOver ? 'text-red-500' : 'text-gray-300'}`}>
          {value.length} / {limit > 0 ? limit : '--'}文字
        </span>
      </div>
      <input 
        type="text" 
        value={value} 
        onChange={e => onChange(e.target.value)} 
        placeholder={limitsText}
        className={`w-full p-3 border rounded-xl text-sm outline-none transition-all ${isOver ? 'border-red-400 bg-red-50' : 'border-gray-100 focus:border-[#D4A373] focus:bg-white'}`}
      />
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div className="border-b-2 border-orange-100 pb-4 mb-6 text-[#3E2723]">
      <h2 className="text-xl font-bold flex items-center gap-3 text-[#3E2723]">{React.cloneElement(icon, { className: "text-[#D4A373]", size: 24 })}{title}</h2>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, error = false }) {
  return (
    <div className="space-y-1 text-[#3E2723]">
      <label className="block text-[10px] font-bold text-gray-400 ml-2 uppercase tracking-wider">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`w-full p-4 bg-gray-50 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[#D4A373] transition-all ${error ? 'border-red-300 bg-red-50' : 'border-gray-100'}`} />
      {error && <p className="text-[9px] text-red-500 ml-2 font-bold italic animate-in fade-in slide-in-from-top-1">正しい形式で入力してください</p>}
    </div>
  );
}

function QtySelector({ label, initial, add, qty, lineTotal, onChange, isFirst = false }) {
  return (
    <div className={`p-5 rounded-3xl border-2 transition-all duration-300 text-[#3E2723] ${qty > 0 ? 'border-[#D4A373] bg-orange-50 shadow-md' : 'border-gray-50 hover:border-orange-100'}`}>
      <div className="flex justify-between items-center mb-3 text-right">
        <div className="flex flex-col text-left">
          <span className="font-bold text-sm flex items-center gap-2">{label}{isFirst && <span className="bg-[#D4A373] text-white text-[8px] px-1.5 py-0.5 rounded-full font-black uppercase tracking-widest">基本制作分</span>}</span>
          <div className="flex gap-3 mt-1 text-xs">
            <span className={`font-bold tracking-tighter italic font-sans ${isFirst ? 'text-[#D4A373]' : 'text-gray-400'}`}>初回1体目: ¥{initial.toLocaleString()}</span>
            <span className={`font-bold tracking-tighter italic ml-2 font-sans ${qty > (isFirst ? 1 : 0) ? 'text-orange-400' : 'text-gray-400'}`}>追加分: ¥{add.toLocaleString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white p-1 rounded-full shadow-inner border border-gray-100 text-gray-400">
          <button onClick={() => onChange(Math.max(0, qty - 1))} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-orange-50 transition-colors cursor-pointer"><Minus size={14} /></button>
          <span className="font-black w-6 text-center text-sm text-[#3E2723]">{qty}</span>
          <button onClick={() => onChange(qty + 1)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-orange-50 transition-colors cursor-pointer"><Plus size={14} /></button>
        </div>
      </div>
      {qty > 0 && (
        <div className="pt-2 border-t border-orange-100 flex justify-between items-center animate-in fade-in slide-in-from-top-1">
          <span className="text-[10px] font-bold text-orange-600 tracking-tighter uppercase text-left">Subtotal / 小計</span>
          <span className="font-black text-lg text-[#D4A373]">¥{lineTotal.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}

function SimpleQtySelector({ icon, label, price, qty, onChange, subtext }) {
  return (
    <div className={`p-4 rounded-2xl border transition-all text-[#3E2723] ${qty > 0 ? 'border-[#D4A373] bg-white ring-1 ring-[#D4A373]' : 'border-gray-100 bg-white'}`}>
      <div className="flex items-center justify-between text-right">
        <div className="flex items-center gap-3 text-left">
          <div className={`p-2 rounded-xl ${qty > 0 ? 'bg-orange-100 text-[#D4A373]' : 'bg-gray-100 text-gray-300'}`}>{icon}</div>
          <div><span className="text-sm font-bold block">{label}</span><p className="text-[9px] text-gray-400 leading-tight mt-1">{subtext}</p></div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[10px] font-bold text-[#D4A373] tracking-tighter">+¥{price ? price.toLocaleString() : '0'}</span>
          <div className="flex items-center gap-3 text-gray-400">
            <button onClick={() => onChange(Math.max(0, qty - 1))} className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center cursor-pointer"><Minus size={12} /></button>
            <span className="font-bold w-4 text-center text-sm text-[#3E2723]">{qty}</span>
            <button onClick={() => onChange(qty + 1)} className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center cursor-pointer"><Plus size={12} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubQtySelector({ label, max, qty, onChange }) {
  return (
    <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-orange-50 shadow-sm text-[#3E2723] text-right">
      <span className="text-sm font-bold tracking-tight text-left">{label}</span>
      <div className="flex items-center gap-3">
        <button onClick={() => onChange(Math.max(0, qty - 1))} className="w-7 h-7 rounded-full border border-gray-100 flex items-center justify-center hover:bg-orange-50 transition-colors text-gray-400 cursor-pointer"><Minus size={10} /></button>
        <span className="font-bold w-8 text-center text-xs text-[#3E2723]">{qty} / {max}</span>
        <button onClick={() => onChange(Math.min(max, qty + 1))} className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:bg-orange-50 transition-colors text-gray-400 cursor-pointer"><Plus size={10} /></button>
      </div>
    </div>
  );
}

function CheckboxItem({ icon, label, price, checked, onChange, disabled = false, subtext }) {
  return (
    <label className={`flex items-center justify-between transition-all text-right ${disabled ? 'opacity-30 cursor-not-allowed' : 'opacity-100 cursor-pointer group'}`}>
      <div className="flex items-center gap-3 text-left text-[#3E2723] flex-1">
        <input type="checkbox" checked={checked} onChange={e => !disabled && onChange(e.target.checked)} className="w-5 h-5 rounded accent-[#D4A373] group-active:scale-90 transition-transform cursor-pointer" />
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl transition-colors ${checked ? 'bg-orange-100 text-[#D4A373]' : 'bg-white text-gray-300 border border-gray-100'}`}>{icon}</div>
          <div><span className="text-sm font-bold block">{label}</span>{subtext && <p className="text-[10px] text-gray-500 leading-tight mt-0.5 font-bold italic">{subtext}</p>}</div>
        </div>
      </div>
      <span className="text-xs font-bold text-[#D4A373] tracking-tighter">+¥{price.toLocaleString()}</span>
    </label>
  );
}

function AgreementItem({ checked, onChange, text, important = false }) {
  return (
    <label className={`flex gap-3 p-3 rounded-2xl cursor-pointer items-start hover:bg-white transition-colors group text-right ${important ? 'bg-orange-50/50' : ''}`}>
      <input type="checkbox" checked={checked} onChange={onChange} className="mt-1 w-5 h-5 accent-red-600 shrink-0 group-active:scale-90 transition-transform cursor-pointer" />
      <span className={`text-[11px] leading-relaxed font-bold text-left ${checked ? 'text-gray-900' : 'text-red-800 opacity-60'} ${important ? 'text-red-900' : ''}`}>
        {important && <span className="bg-red-600 text-white px-1 rounded text-[8px] mr-1 inline-block align-middle shadow-sm">最重要</span>}
        {text}
      </span>
    </label>
  );
}

function SummaryRow({ label, price, highlight = false }) {
  return (
    <div className={`flex justify-between text-sm text-right ${highlight ? 'text-[#D4A373] font-bold' : 'text-gray-600'}`}>
      <span className="flex items-center gap-1 text-left"><ChevronRight size={12} /> {label}</span>
      <span className="font-bold tracking-tight">¥{price.toLocaleString()}</span>
    </div>
  );
}

export default function App() {
  const [currentPage, setCurrentPage] = useState(() => {
    // ページアクセス時にURLのハッシュを取得し、対象ページであれば初期値としてセットする
    const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
    const validPages = ['home', 'b2b', 'faq-detail', 'terms'];
    return validPages.includes(hash) ? hash : 'home';
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // 移動に合わせてメニューの順序を最適化
  const menuItems = [
    { id: 'hero', label: 'トップページ' },
    { id: 'story', label: '形に残す理由' },
    { id: 'before-after', label: '完成イメージ' },
    { id: 'quality', label: 'AIだけではない点' },
    { id: 'size-price', label: 'サイズと価格' },
    { id: 'photo-guide', label: '写真送付の注意点' },
    { id: 'volume-discount', label: '2体目以降の追加価格' },
    { id: 'options', label: 'オプション' },
    { id: 'core-value', label: '体験価値' },
    { id: 'ar-experience', label: 'AR体験' },
    { id: 'flow', label: '制作の流れ' },
    { id: 'faq', label: 'FAQ（抜粋）' },
    { id: 'csr', label: '社会貢献' },
    { id: 'b2b', label: 'BtoB導入案内' },
    { id: 'order-form', label: '注文フォーム' },
  ];

  useEffect(() => {
    // 以前の強制リセットを廃止し、アクセス時の状態をブラウザの履歴に記録する
    window.history.replaceState({ page: currentPage }, '', window.location.hash || '#home');

    const handlePopState = (event) => {
      if (event.state && event.state.page) {
        setCurrentPage(event.state.page);
      } else {
        // 戻る/進むボタンなどで状態がない場合はハッシュから復元
        const hash = window.location.hash.replace('#', '');
        const validPages = ['home', 'b2b', 'faq-detail', 'terms'];
        setCurrentPage(validPages.includes(hash) ? hash : 'home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateTo = (page) => {
    setCurrentPage(page);
    window.history.pushState({ page }, '', `#${page}`);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 10);
  };

  const scrollToSection = (id) => {
    setIsMenuOpen(false);
    if (currentPage !== 'home') {
      setCurrentPage('home');
      window.history.pushState({ page: 'home' }, '', `#home`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  return (
    // ★追加: lang="ja", translate="no", className="notranslate" を追加して自動翻訳をブロックします
    <div className="notranslate min-h-screen font-sans text-gray-900 w-full overflow-x-hidden relative tracking-tight bg-white" lang="ja" translate="no">
      <style dangerouslySetInnerHTML={{
        __html: `
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700;900&display=swap');
        body { font-family: 'Noto Sans JP', sans-serif; scroll-behavior: smooth; }
        
        /* Makuakeバッジ用のキラキラエフェクトアニメーション */
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        .animate-shimmer {
          animation: shimmer 2.5s infinite;
        }
      `}} />

      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md z-50 border-b border-gray-200 h-16 md:h-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-full relative z-50">
          
          <div className="flex items-center cursor-pointer group" onClick={() => scrollToSection('hero')}>
            <img 
              src="/image/ロゴ.png" 
              alt="富士教材" 
              className="h-8 md:h-10 w-auto object-contain transition-opacity group-hover:opacity-80" 
              onError={(e)=>{
                e.target.onerror = null; 
                e.target.src="https://placehold.co/200x50/white/e20000?text=Fuji+Kyozai";
              }} 
            />
          </div>

          <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
            {/* ★ 追加: ヘッダーのInstagramボタン（PC・タブレット用） */}
            <a 
              href="https://www.instagram.com/uchinoko_figure/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hidden md:flex items-center gap-1.5 bg-gradient-to-r from-pink-50 to-orange-50 hover:opacity-80 text-gray-800 border border-pink-200 px-4 py-2 rounded-full font-bold text-sm transition-opacity shadow-sm"
            >
              <Instagram className="w-4 h-4 text-pink-600" />
              <span>Instagram</span>
            </a>

            <button onClick={() => scrollToSection('order-form')} className="hidden sm:flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-full font-bold text-sm transition-transform hover:scale-105 shadow-[0_4px_15px_rgba(220,38,38,0.4)]">
              <Package className="w-4 h-4" />
              <span>注文する</span>
            </button>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="flex items-center gap-1 md:gap-2 text-gray-800 hover:text-gray-600 transition-colors bg-gray-50 px-3 md:px-5 py-2 rounded-full font-bold text-xs md:text-sm border border-gray-200">
              <span className="tracking-widest uppercase">Menu</span>
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className={`absolute top-16 md:top-20 left-0 w-full bg-white border-b border-gray-200 shadow-lg transition-all duration-300 overflow-hidden ${isMenuOpen ? 'max-h-[calc(100vh-64px)] md:max-h-[calc(100vh-80px)] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
          <div className="max-w-7xl mx-auto px-4 py-6 md:py-10 max-h-[calc(100vh-64px)] overflow-y-auto">
            
            <div className="flex flex-col gap-6 md:gap-8">
              
              <div className="w-full">
                {menuItems.filter(i => i.id === 'order-form').map((item) => (
                  <button key={item.id} onClick={() => scrollToSection(item.id)} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-2xl py-4 md:py-6 flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] shadow-md border-2 border-red-500">
                    <span className="font-black text-xl md:text-2xl tracking-widest">{item.label}</span>
                    <ChevronRight className="w-6 h-6" />
                  </button>
                ))}
              </div>

              <div>
                <h3 className="text-xs font-bold text-gray-400 mb-3 tracking-widest uppercase flex items-center gap-2"><span className="w-full h-[1px] bg-gray-200"></span>Price & Discount<span className="w-full h-[1px] bg-gray-200"></span></h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  {menuItems.filter(i => ['size-price', 'volume-discount'].includes(i.id)).map((item) => (
                    <button key={item.id} onClick={() => scrollToSection(item.id)} className={`p-4 md:p-6 rounded-2xl border transition-colors flex items-center justify-between group bg-white hover:bg-gray-50 border-gray-200`}>
                      <div className="flex items-center gap-3">
                        {item.id === 'volume-discount' ? <Gift className="w-6 h-6 text-gray-700" /> : <Tag className="w-6 h-6 text-gray-700" />}
                        <span className="font-black text-base md:text-lg text-gray-900">{item.label}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold text-gray-400 mb-3 tracking-widest uppercase flex items-center gap-2"><span className="w-full h-[1px] bg-gray-200"></span>Features<span className="w-full h-[1px] bg-gray-200"></span></h3>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 md:gap-3">
                  {menuItems.filter(i => ['story', 'before-after', 'core-value', 'ar-experience', 'quality'].includes(i.id)).map((item) => (
                    <button key={item.id} onClick={() => scrollToSection(item.id)} className="bg-gray-50 border border-gray-200 hover:bg-gray-100 p-3 md:p-4 rounded-xl transition-colors flex flex-col items-center justify-center gap-1 md:gap-2">
                      <span className="font-bold text-sm text-gray-800 text-center">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold text-gray-400 mb-3 tracking-widest uppercase flex items-center gap-2"><span className="w-full h-[1px] bg-gray-200"></span>Information<span className="w-full h-[1px] bg-gray-200"></span></h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {menuItems.filter(i => !['order-form', 'size-price', 'volume-discount', 'story', 'before-after', 'core-value', 'ar-experience', 'quality'].includes(i.id)).map((item) => (
                    <button key={item.id} onClick={() => scrollToSection(item.id)} className="text-left px-3 py-3 md:px-4 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors font-bold text-xs md:text-sm text-gray-600 flex items-center justify-between">
                      <span className="truncate">{item.label}</span>
                      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                    </button>
                  ))}
                  {/* メニュー内にもInstagramリンクを追加 */}
                  <a href="https://www.instagram.com/uchinoko_figure/" target="_blank" rel="noopener noreferrer" className="text-left px-3 py-3 md:px-4 rounded-lg bg-gradient-to-r from-pink-50 to-orange-50 border border-pink-100 hover:opacity-80 transition-opacity font-bold text-xs md:text-sm text-gray-800 flex items-center justify-between col-span-2 md:col-span-1">
                    <span className="truncate flex items-center gap-1.5"><Instagram className="w-4 h-4 text-pink-600"/> 公式Instagram</span>
                    <ExternalLink className="w-4 h-4 text-pink-400 shrink-0" />
                  </a>
                </div>
              </div>

            </div>

            <div className="mt-8 md:mt-12 flex justify-center">
              <button onClick={() => setIsMenuOpen(false)} className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 rounded-full py-2 px-8 font-bold tracking-widest transition-colors flex items-center gap-2 text-sm">
                <X className="w-4 h-4" /> 閉じる
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        {currentPage === 'home' && <HomePage navigateTo={navigateTo} scrollToSection={scrollToSection} />}
        {currentPage === 'b2b' && <div className="pt-32 pb-20 text-center px-4"><h1 className="text-3xl font-black mb-6 text-gray-900">法人様向け 導入のご案内</h1><p className="mb-10 text-gray-600 font-bold">詳細は準備中です。</p><button onClick={() => navigateTo('home')} className="text-gray-600 font-bold hover:underline bg-white px-6 py-3 rounded-full border border-gray-200">← トップページへ戻る</button></div>}
        
        {currentPage === 'faq-detail' && (
          <div className="pt-32 pb-20 px-4 max-w-4xl mx-auto">
            <h1 className="text-3xl font-black mb-8 text-gray-900 text-center">よくあるご質問（FAQ）</h1>
            <div className="bg-white p-8 rounded-2xl border border-gray-200 text-sm leading-relaxed text-gray-700">
              {faqData.map((item, index) => (
                <div key={index} className="mb-8 last:mb-0">
                  <h3 className="font-black text-gray-900 mb-3 text-base flex items-start gap-2">
                    <span className="text-gray-400 shrink-0">Q.</span>
                    {item.q}
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-xl flex items-start gap-2 border border-gray-100">
                    <span className="font-black text-gray-400 shrink-0">A.</span>
                    <p className="whitespace-pre-wrap">{formatText(item.a)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <button onClick={() => navigateTo('home')} className="text-gray-600 font-bold hover:underline bg-white px-6 py-3 rounded-full border border-gray-200">← トップページへ戻る</button>
            </div>
          </div>
        )}
        
        {currentPage === 'terms' && (
          <div className="pt-32 pb-20 px-4 max-w-4xl mx-auto">
            <h1 className="text-3xl font-black mb-8 text-gray-900 text-center">「うちの子ペイントフィギュア」利用規約</h1>
            <div className="bg-white p-8 rounded-2xl border border-gray-200 text-sm leading-relaxed text-gray-700">
              <p className="mb-8 whitespace-pre-wrap">本規約は、富士教材有限会社（以下「当社」）が提供する「うちの子ペイントフィギュア」（以下「本サービス」）の利用条件を定めるものです。利用者は、本規約に同意の上で本サービスを利用するものとします。</p>
              
              {termsData.map((item, index) => (
                <div key={index} className="mb-8">
                  <h3 className="font-black text-lg text-gray-900 mb-3 border-b border-gray-200 pb-2">{item.title}</h3>
                  <p className="whitespace-pre-wrap">{item.content}</p>
                </div>
              ))}

              <div className="mt-12 text-right font-bold">
                <p>2026年5月1日 制定</p>
                <p>富士教材有限会社</p>
              </div>
            </div>
            <div className="mt-10 text-center">
              <button onClick={() => navigateTo('home')} className="text-gray-600 font-bold hover:underline bg-white px-6 py-3 rounded-full border border-gray-200">← トップページへ戻る</button>
            </div>
          </div>
        )}
      </main>

      <footer className="bg-white text-gray-700 py-12 md:py-16 text-center md:text-left border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center">
          
          <div>
            <img 
              src="/image/ロゴ.png" 
              alt="富士教材有限会社" 
              className="h-8 md:h-10 w-auto object-contain mb-4 mx-auto md:mx-0" 
              onError={(e)=>{
                e.target.onerror = null; 
                e.target.src="https://placehold.co/200x50/white/e20000?text=Fuji+Kyozai";
              }} 
            />
            <p className="text-xs md:text-sm text-gray-600 leading-relaxed">〒790-0012 愛媛県松山市柳井町3丁目8-9<br />1954年創業。最新の3D技術で新しい絆を形に。</p>
          </div>

          <div className="md:text-right flex flex-col items-center md:items-end gap-4">
            {/* フッターにInstagramリンクを追加 */}
            <div className="flex items-center gap-4 mb-2">
              <a href="https://www.instagram.com/uchinoko_figure/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-600 transition-colors flex items-center gap-2 font-bold text-sm">
                <Instagram className="w-6 h-6" /> <span className="md:hidden">公式Instagram</span>
              </a>
            </div>
            <button onClick={() => navigateTo('terms')} className="text-sm text-gray-500 hover:text-red-600 transition-colors underline underline-offset-4 font-bold">利用規約</button>
            <a href="https://fujikyouzai.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 px-6 py-3 rounded-full text-xs md:text-sm font-bold transition-colors tracking-widest text-gray-800 border border-gray-300 shadow-sm">公式サイトへ <ExternalLink className="w-4 h-4 text-gray-500" /></a>
          </div>
        </div>
      </footer>
    </div>
  );
}